import * as z from 'zod';

export const CreateServiceRequest = z.object({
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z.enum(['APPOINTMENT', 'CLASS', 'COURSE']).optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z.enum(['ONLINE', 'IN_PERSON']).optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z.enum(['CUSTOM', 'BUSINESS', 'CUSTOMER']).optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        )
        .optional()
        .nullable(),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z.enum(['BUSINESS', 'BILLING']).optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('Service to create.'),
});
export const CreateServiceResponse = z.object({
  _id: z
    .string()
    .describe('Service ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  appId: z
    .string()
    .describe(
      'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  createdByAppId: z
    .string()
    .describe(
      "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  type: z
    .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
    .describe(
      'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
    )
    .optional(),
  sortOrder: z
    .number()
    .int()
    .describe(
      'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Service name.')
    .max(400)
    .min(1)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
    )
    .max(7000)
    .optional()
    .nullable(),
  tagLine: z
    .string()
    .describe('Short service description, such as `Hair styling`.')
    .max(6000)
    .optional()
    .nullable(),
  defaultCapacity: z
    .number()
    .int()
    .describe(
      'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
    )
    .min(1)
    .max(1000)
    .optional()
    .nullable(),
  media: z
    .object({
      items: z
        .array(
          z.intersection(
            z.object({}),
            z.xor([
              z.object({ image: z.never().optional() }),
              z.object({
                image: z
                  .string()
                  .describe(
                    'Details of the image associated with the service, such as URL and size.'
                  ),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z
                .string()
                .describe(
                  'Details of the image associated with the service, such as URL and size.'
                ),
            }),
          ])
        )
        .describe('Primary media associated with the service.')
        .optional(),
      coverMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z
                .string()
                .describe(
                  'Details of the image associated with the service, such as URL and size.'
                ),
            }),
          ])
        )
        .describe('Cover media associated with the service.')
        .optional(),
    })
    .describe('Media associated with the service.')
    .optional(),
  hidden: z
    .boolean()
    .describe(
      'Whether the service is hidden from Wix Bookings pages and widgets.'
    )
    .optional()
    .nullable(),
  category: z
    .object({
      _id: z
        .string()
        .describe('Category ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z
        .string()
        .describe('Category name.')
        .max(500)
        .optional()
        .nullable(),
      sortOrder: z
        .number()
        .int()
        .describe('Order of a category within a category list.')
        .optional()
        .nullable(),
    })
    .describe(
      "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
    )
    .optional(),
  form: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe('Form the customer filled out when booking the service.')
    .optional(),
  payment: z
    .intersection(
      z.object({
        rateType: z
          .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
          .describe('The rate the customer is expected to pay for the service.')
          .optional(),
        options: z
          .object({
            online: z
              .boolean()
              .describe(
                'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
              )
              .optional()
              .nullable(),
            inPerson: z
              .boolean()
              .describe('Customers can pay for the service in person.')
              .optional()
              .nullable(),
            deposit: z
              .boolean()
              .describe(
                'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
              )
              .optional()
              .nullable(),
            pricingPlan: z
              .boolean()
              .describe(
                'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'The payment options a customer can use to pay for the service.'
          )
          .optional(),
        pricingPlanIds: z.array(z.string()).max(75).optional(),
        addOnOption: z
          .enum(['ONLINE', 'IN_PERSON'])
          .describe(
            'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          fixed: z.never().optional(),
          custom: z.never().optional(),
          varied: z.never().optional(),
        }),
        z.object({
          custom: z.never().optional(),
          varied: z.never().optional(),
          fixed: z
            .object({
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                )
                .optional(),
              deposit: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                )
                .optional(),
              fullUpfrontPaymentAllowed: z
                .boolean()
                .describe(
                  'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
            ),
        }),
        z.object({
          fixed: z.never().optional(),
          varied: z.never().optional(),
          custom: z
            .object({
              description: z
                .string()
                .describe(
                  'A custom description explaining to the customer how to pay for the service.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
            ),
        }),
        z.object({
          fixed: z.never().optional(),
          custom: z.never().optional(),
          varied: z
            .object({
              defaultPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                )
                .optional(),
              deposit: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                )
                .optional(),
              minPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The minimal price a customer may pay for this service, based on its variants.'
                )
                .optional(),
              maxPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The maximum price a customer may pay for this service, based on its variants.'
                )
                .optional(),
              fullUpfrontPaymentAllowed: z
                .boolean()
                .describe(
                  'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
            ),
        }),
      ])
    )
    .describe(
      'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
    )
    .optional(),
  onlineBooking: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
        )
        .optional()
        .nullable(),
      requireManualApproval: z
        .boolean()
        .describe('Booking the service requires approval by the Wix user.')
        .optional()
        .nullable(),
      allowMultipleRequests: z
        .boolean()
        .describe(
          'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Online booking settings.')
    .optional(),
  conferencing: z
    .object({
      enabled: z
        .boolean()
        .describe(
          "Whether a conference link is generated for the service's sessions."
        )
        .optional()
        .nullable(),
    })
    .describe('Conferencing options for the service.')
    .optional(),
  locations: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Location ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          type: z
            .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
            .describe('Location type.\n\nDefault: `CUSTOM`')
            .optional(),
          calculatedAddress: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
            })
            .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
            .optional(),
        }),
        z.xor([
          z.object({
            business: z.never().optional(),
            custom: z.never().optional(),
          }),
          z.object({
            custom: z.never().optional(),
            business: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Business location name.')
                  .max(150)
                  .optional(),
                default: z
                  .boolean()
                  .describe(
                    'Whether this is the default location. There can only be a single default location per site.'
                  )
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe('Business location address.')
                  .optional(),
                email: z
                  .string()
                  .describe('Business location email.')
                  .email()
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Business location phone.')
                  .optional()
                  .nullable(),
              })
              .describe('Information about business locations.'),
          }),
          z.object({
            business: z.never().optional(),
            custom: z
              .object({
                _id: z
                  .string()
                  .describe('ID of the custom location.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe('Address of the custom location.')
                  .optional(),
              })
              .describe('Information about custom locations.'),
          }),
        ])
      )
    )
    .max(500)
    .optional(),
  bookingPolicy: z
    .object({
      _id: z
        .string()
        .describe('The ID to the policy for the booking.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the policy was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the policy was updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Name of the policy.')
        .max(400)
        .optional()
        .nullable(),
      customPolicyDescription: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
            )
            .optional(),
          description: z
            .string()
            .describe(
              'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
            )
            .max(2500)
            .optional(),
        })
        .describe(
          'Custom description for the policy. This policy is displayed to the participant.'
        )
        .optional(),
      default: z
        .boolean()
        .describe('Whether the policy is the default for the meta site.')
        .optional()
        .nullable(),
      limitEarlyBookingPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
            )
            .optional(),
          earliestBookingInMinutes: z
            .number()
            .int()
            .describe(
              'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy for limiting early bookings.')
        .optional(),
      limitLateBookingPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
            )
            .optional(),
          latestBookingInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy for limiting late bookings.')
        .optional(),
      bookAfterStartPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Policy on booking an entity after the start of the schedule.'
        )
        .optional(),
      cancellationPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
            )
            .optional(),
          limitLatestCancellation: z
            .boolean()
            .describe(
              'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
            )
            .optional(),
          latestCancellationInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
          allowAnonymous: z
            .boolean()
            .describe(
              "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
            )
            .optional()
            .nullable(),
        })
        .describe('Policy for canceling a booked entity.')
        .optional(),
      reschedulePolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
            )
            .optional(),
          limitLatestReschedule: z
            .boolean()
            .describe(
              'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
            )
            .optional(),
          latestRescheduleInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
          allowAnonymous: z
            .boolean()
            .describe(
              "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
            )
            .optional()
            .nullable(),
        })
        .describe('Policy for rescheduling a booked entity.')
        .optional(),
      waitlistPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
            )
            .optional(),
          capacity: z
            .number()
            .int()
            .describe(
              'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
            )
            .min(1)
            .optional(),
          reservationTimeInMinutes: z
            .number()
            .int()
            .describe(
              'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
            )
            .min(1)
            .optional(),
        })
        .describe('Waitlist policy for the service.')
        .optional(),
      participantsPolicy: z
        .object({
          maxParticipantsPerBooking: z
            .number()
            .int()
            .describe(
              'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy regarding the participants per booking.')
        .optional(),
      resourcesPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
            )
            .optional(),
          autoAssignAllowed: z
            .boolean()
            .describe(
              '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe('Policy for allocating resources.')
        .optional(),
      cancellationFeePolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
            )
            .optional(),
          cancellationWindows: z
            .array(
              z.intersection(
                z.object({
                  startInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'The fee will be applied if the booked session starts within this start time in minutes.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    amount: z.never().optional(),
                    percentage: z.never().optional(),
                  }),
                  z.object({
                    percentage: z.never().optional(),
                    amount: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe('Amount to be charged as a cancellation fee.'),
                  }),
                  z.object({
                    amount: z.never().optional(),
                    percentage: z
                      .string()
                      .describe(
                        'Percentage of the original price to be charged as a cancellation fee.'
                      ),
                  }),
                ])
              )
            )
            .max(2)
            .optional(),
          autoCollectFeeEnabled: z
            .boolean()
            .describe(
              'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
            )
            .optional()
            .nullable(),
        })
        .describe('Rules for cancellation fees.')
        .optional(),
      saveCreditCardPolicy: z
        .object({
          enabled: z.boolean().describe('Default: `false`').optional(),
        })
        .describe('Rule for saving credit card.')
        .optional(),
    })
    .describe(
      '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
    )
    .optional(),
  schedule: z
    .object({
      _id: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      firstSessionStart: z
        .date()
        .describe(
          'Start time of the first session in the schedule. For courses only.'
        )
        .optional()
        .nullable(),
      lastSessionEnd: z
        .date()
        .describe(
          'End time of the last session in the schedule. For courses only.'
        )
        .optional()
        .nullable(),
      availabilityConstraints: z
        .object({
          durations: z
            .array(
              z.object({
                minutes: z
                  .number()
                  .int()
                  .describe(
                    'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                  )
                  .min(1)
                  .max(44639)
                  .optional(),
              })
            )
            .max(50)
            .optional(),
          sessionDurations: z.array(z.number().int()).max(50).optional(),
          timeBetweenSessions: z
            .number()
            .int()
            .describe(
              'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
            )
            .min(0)
            .max(720)
            .optional(),
        })
        .describe('Limitations affecting the service availability.')
        .optional(),
    })
    .describe(
      "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
    )
    .optional(),
  staffMemberIds: z.array(z.string()).max(220).optional(),
  serviceResources: z
    .array(
      z.object({
        resourceType: z
          .object({
            _id: z
              .string()
              .describe('The type of the resource.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('The name of the resource type.')
              .max(40)
              .min(1)
              .optional()
              .nullable(),
          })
          .describe(
            'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
          )
          .optional(),
      })
    )
    .max(3)
    .optional(),
  supportedSlugs: z
    .array(
      z.object({
        name: z
          .string()
          .describe(
            "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
          )
          .max(500)
          .optional(),
        custom: z
          .boolean()
          .describe(
            'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the slug was created. This is a system field.'
          )
          .optional()
          .nullable(),
      })
    )
    .max(100)
    .optional(),
  mainSlug: z
    .object({
      name: z
        .string()
        .describe(
          "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
        )
        .max(500)
        .optional(),
      custom: z
        .boolean()
        .describe(
          'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the slug was created. This is a system field.')
        .optional()
        .nullable(),
    })
    .describe(
      'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
    )
    .optional(),
  urls: z
    .object({
      servicePage: z
        .string()
        .describe('The URL for the service page.')
        .optional(),
      bookingPage: z
        .string()
        .describe(
          'The URL for the booking entry point. It can be either to the calendar or to the service page.'
        )
        .optional(),
      calendarPage: z
        .string()
        .describe(
          'The URL for the calendar. Can be empty if no calendar exists.'
        )
        .optional(),
    })
    .describe(
      'URLs to various service-related pages, such as the calendar page and the booking page.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling users to save custom data related to the service.'
    )
    .optional(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('Custom SEO data for the service.')
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
    )
    .optional()
    .nullable(),
  addOnGroups: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of the add-on group.')
          .max(100)
          .optional()
          .nullable(),
        maxNumberOfAddOns: z
          .number()
          .int()
          .describe(
            "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
          )
          .optional()
          .nullable(),
        addOnIds: z.array(z.string()).max(7).optional(),
        prompt: z
          .string()
          .describe(
            "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
          )
          .max(200)
          .optional()
          .nullable(),
      })
    )
    .max(3)
    .optional(),
  addOnDetails: z
    .array(
      z.object({
        addOnId: z
          .string()
          .describe('ID of the add-on.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        durationInMinutes: z
          .number()
          .int()
          .describe(
            'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
          )
          .optional()
          .nullable(),
      })
    )
    .max(50)
    .optional(),
  taxableAddress: z
    .object({
      taxableAddressType: z
        .enum(['BUSINESS', 'BILLING'])
        .describe('Taxable address type.')
        .optional(),
    })
    .describe('Taxable address used to calculate tax')
    .optional(),
});
export const BulkCreateServicesRequest = z.object({
  services: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Service ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appId: z
          .string()
          .describe(
            'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        type: z.enum(['APPOINTMENT', 'CLASS', 'COURSE']).optional(),
        sortOrder: z
          .number()
          .int()
          .describe(
            'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Service name.')
          .max(400)
          .min(1)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
          )
          .max(7000)
          .optional()
          .nullable(),
        tagLine: z
          .string()
          .describe('Short service description, such as `Hair styling`.')
          .max(6000)
          .optional()
          .nullable(),
        defaultCapacity: z
          .number()
          .int()
          .describe(
            'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        media: z
          .object({
            items: z
              .array(
                z.intersection(
                  z.object({}),
                  z.xor([
                    z.object({ image: z.never().optional() }),
                    z.object({
                      image: z
                        .string()
                        .describe(
                          'Details of the image associated with the service, such as URL and size.'
                        ),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            mainMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Primary media associated with the service.')
              .optional(),
            coverMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Cover media associated with the service.')
              .optional(),
          })
          .describe('Media associated with the service.')
          .optional(),
        hidden: z
          .boolean()
          .describe(
            'Whether the service is hidden from Wix Bookings pages and widgets.'
          )
          .optional()
          .nullable(),
        category: z
          .object({
            _id: z
              .string()
              .describe('Category ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            name: z
              .string()
              .describe('Category name.')
              .max(500)
              .optional()
              .nullable(),
            sortOrder: z
              .number()
              .int()
              .describe('Order of a category within a category list.')
              .optional()
              .nullable(),
          })
          .describe(
            "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
          )
          .optional(),
        form: z
          .object({
            _id: z
              .string()
              .describe(
                'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Form the customer filled out when booking the service.')
          .optional(),
        payment: z
          .intersection(
            z.object({
              rateType: z
                .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                .optional(),
              options: z
                .object({
                  online: z
                    .boolean()
                    .describe(
                      'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                    )
                    .optional()
                    .nullable(),
                  inPerson: z
                    .boolean()
                    .describe('Customers can pay for the service in person.')
                    .optional()
                    .nullable(),
                  deposit: z
                    .boolean()
                    .describe(
                      'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                    )
                    .optional()
                    .nullable(),
                  pricingPlan: z
                    .boolean()
                    .describe(
                      'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The payment options a customer can use to pay for the service.'
                )
                .optional(),
              pricingPlanIds: z.array(z.string()).max(75).optional(),
              addOnOption: z.enum(['ONLINE', 'IN_PERSON']).optional(),
            }),
            z.xor([
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                varied: z.never().optional(),
                fixed: z
                  .object({
                    price: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                varied: z.never().optional(),
                custom: z
                  .object({
                    description: z
                      .string()
                      .describe(
                        'A custom description explaining to the customer how to pay for the service.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z
                  .object({
                    defaultPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    minPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The minimal price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    maxPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The maximum price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                  ),
              }),
            ])
          )
          .describe(
            'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
          )
          .optional(),
        onlineBooking: z
          .object({
            enabled: z
              .boolean()
              .describe(
                'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
              )
              .optional()
              .nullable(),
            requireManualApproval: z
              .boolean()
              .describe(
                'Booking the service requires approval by the Wix user.'
              )
              .optional()
              .nullable(),
            allowMultipleRequests: z
              .boolean()
              .describe(
                'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Online booking settings.')
          .optional(),
        conferencing: z
          .object({
            enabled: z
              .boolean()
              .describe(
                "Whether a conference link is generated for the service's sessions."
              )
              .optional()
              .nullable(),
          })
          .describe('Conferencing options for the service.')
          .optional(),
        locations: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Location ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                type: z.enum(['CUSTOM', 'BUSINESS', 'CUSTOMER']).optional(),
                calculatedAddress: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe(
                    'Location address. Empty for `{"type": "CUSTOMER"}`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  business: z.never().optional(),
                  custom: z.never().optional(),
                }),
                z.object({
                  custom: z.never().optional(),
                  business: z
                    .object({
                      _id: z
                        .string()
                        .describe(
                          'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      name: z
                        .string()
                        .describe('Business location name.')
                        .max(150)
                        .optional(),
                      default: z
                        .boolean()
                        .describe(
                          'Whether this is the default location. There can only be a single default location per site.'
                        )
                        .optional()
                        .nullable(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Business location address.')
                        .optional(),
                      email: z
                        .string()
                        .describe('Business location email.')
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Business location phone.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Information about business locations.'),
                }),
                z.object({
                  business: z.never().optional(),
                  custom: z
                    .object({
                      _id: z
                        .string()
                        .describe('ID of the custom location.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Address of the custom location.')
                        .optional(),
                    })
                    .describe('Information about custom locations.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        bookingPolicy: z
          .object({
            _id: z
              .string()
              .describe('The ID to the policy for the booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the policy was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the policy was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the policy.')
              .max(400)
              .optional()
              .nullable(),
            customPolicyDescription: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                  )
                  .optional(),
                description: z
                  .string()
                  .describe(
                    'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                  )
                  .max(2500)
                  .optional(),
              })
              .describe(
                'Custom description for the policy. This policy is displayed to the participant.'
              )
              .optional(),
            default: z
              .boolean()
              .describe('Whether the policy is the default for the meta site.')
              .optional()
              .nullable(),
            limitEarlyBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                  )
                  .optional(),
                earliestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting early bookings.')
              .optional(),
            limitLateBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                  )
                  .optional(),
                latestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting late bookings.')
              .optional(),
            bookAfterStartPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                'Policy on booking an entity after the start of the schedule.'
              )
              .optional(),
            cancellationPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestCancellation: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestCancellationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for canceling a booked entity.')
              .optional(),
            reschedulePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestReschedule: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestRescheduleInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for rescheduling a booked entity.')
              .optional(),
            waitlistPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                  )
                  .optional(),
                capacity: z
                  .number()
                  .int()
                  .describe(
                    'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
                reservationTimeInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Waitlist policy for the service.')
              .optional(),
            participantsPolicy: z
              .object({
                maxParticipantsPerBooking: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy regarding the participants per booking.')
              .optional(),
            resourcesPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                  )
                  .optional(),
                autoAssignAllowed: z
                  .boolean()
                  .describe(
                    '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe('Policy for allocating resources.')
              .optional(),
            cancellationFeePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                  )
                  .optional(),
                cancellationWindows: z
                  .array(
                    z.intersection(
                      z.object({
                        startInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The fee will be applied if the booked session starts within this start time in minutes.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Amount to be charged as a cancellation fee.'
                            ),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              'Percentage of the original price to be charged as a cancellation fee.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(2)
                  .optional(),
                autoCollectFeeEnabled: z
                  .boolean()
                  .describe(
                    'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Rules for cancellation fees.')
              .optional(),
            saveCreditCardPolicy: z
              .object({
                enabled: z.boolean().describe('Default: `false`').optional(),
              })
              .describe('Rule for saving credit card.')
              .optional(),
          })
          .describe(
            '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
          )
          .optional(),
        schedule: z
          .object({
            _id: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstSessionStart: z
              .date()
              .describe(
                'Start time of the first session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            lastSessionEnd: z
              .date()
              .describe(
                'End time of the last session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            availabilityConstraints: z
              .object({
                durations: z
                  .array(
                    z.object({
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                        )
                        .min(1)
                        .max(44639)
                        .optional(),
                    })
                  )
                  .max(50)
                  .optional(),
                sessionDurations: z.array(z.number().int()).max(50).optional(),
                timeBetweenSessions: z
                  .number()
                  .int()
                  .describe(
                    'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                  )
                  .min(0)
                  .max(720)
                  .optional(),
              })
              .describe('Limitations affecting the service availability.')
              .optional(),
          })
          .describe(
            "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
          )
          .optional(),
        staffMemberIds: z.array(z.string()).max(220).optional(),
        serviceResources: z
          .array(
            z.object({
              resourceType: z
                .object({
                  _id: z
                    .string()
                    .describe('The type of the resource.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('The name of the resource type.')
                    .max(40)
                    .min(1)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                )
                .optional(),
            })
          )
          .max(3)
          .optional(),
        supportedSlugs: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                )
                .max(500)
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the slug was created. This is a system field.'
                )
                .optional()
                .nullable(),
            })
          )
          .max(100)
          .optional(),
        mainSlug: z
          .object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
          )
          .optional(),
        urls: z
          .object({
            servicePage: z
              .string()
              .describe('The URL for the service page.')
              .optional(),
            bookingPage: z
              .string()
              .describe(
                'The URL for the booking entry point. It can be either to the calendar or to the service page.'
              )
              .optional(),
            calendarPage: z
              .string()
              .describe(
                'The URL for the calendar. Can be empty if no calendar exists.'
              )
              .optional(),
          })
          .describe(
            'URLs to various service-related pages, such as the calendar page and the booking page.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling users to save custom data related to the service.'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Custom SEO data for the service.')
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
          )
          .optional()
          .nullable(),
        addOnGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe(
                  'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Name of the add-on group.')
                .max(100)
                .optional()
                .nullable(),
              maxNumberOfAddOns: z
                .number()
                .int()
                .describe(
                  "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                )
                .optional()
                .nullable(),
              addOnIds: z.array(z.string()).max(7).optional(),
              prompt: z
                .string()
                .describe(
                  "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                )
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(3)
          .optional(),
        addOnDetails: z
          .array(
            z.object({
              addOnId: z
                .string()
                .describe('ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                )
                .optional()
                .nullable(),
            })
          )
          .max(50)
          .optional(),
        taxableAddress: z
          .object({
            taxableAddressType: z.enum(['BUSINESS', 'BILLING']).optional(),
          })
          .describe('Taxable address used to calculate tax')
          .optional(),
      })
    )
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return the created service objects.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateServicesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Update metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Service ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe(
                'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            createdByAppId: z
              .string()
              .describe(
                "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
              .describe(
                'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
              )
              .optional(),
            sortOrder: z
              .number()
              .int()
              .describe(
                'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Service name.')
              .max(400)
              .min(1)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe(
                'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
              )
              .max(7000)
              .optional()
              .nullable(),
            tagLine: z
              .string()
              .describe('Short service description, such as `Hair styling`.')
              .max(6000)
              .optional()
              .nullable(),
            defaultCapacity: z
              .number()
              .int()
              .describe(
                'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
              )
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
            media: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({ image: z.never().optional() }),
                        z.object({
                          image: z
                            .string()
                            .describe(
                              'Details of the image associated with the service, such as URL and size.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                mainMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Primary media associated with the service.')
                  .optional(),
                coverMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Cover media associated with the service.')
                  .optional(),
              })
              .describe('Media associated with the service.')
              .optional(),
            hidden: z
              .boolean()
              .describe(
                'Whether the service is hidden from Wix Bookings pages and widgets.'
              )
              .optional()
              .nullable(),
            category: z
              .object({
                _id: z
                  .string()
                  .describe('Category ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Category name.')
                  .max(500)
                  .optional()
                  .nullable(),
                sortOrder: z
                  .number()
                  .int()
                  .describe('Order of a category within a category list.')
                  .optional()
                  .nullable(),
              })
              .describe(
                "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
              )
              .optional(),
            form: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Form the customer filled out when booking the service.'
              )
              .optional(),
            payment: z
              .intersection(
                z.object({
                  rateType: z
                    .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                    .describe(
                      'The rate the customer is expected to pay for the service.'
                    )
                    .optional(),
                  options: z
                    .object({
                      online: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                        )
                        .optional()
                        .nullable(),
                      inPerson: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service in person.'
                        )
                        .optional()
                        .nullable(),
                      deposit: z
                        .boolean()
                        .describe(
                          'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                        )
                        .optional()
                        .nullable(),
                      pricingPlan: z
                        .boolean()
                        .describe(
                          'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The payment options a customer can use to pay for the service.'
                    )
                    .optional(),
                  pricingPlanIds: z.array(z.string()).max(75).optional(),
                  addOnOption: z
                    .enum(['ONLINE', 'IN_PERSON'])
                    .describe(
                      'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                    fixed: z
                      .object({
                        price: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    varied: z.never().optional(),
                    custom: z
                      .object({
                        description: z
                          .string()
                          .describe(
                            'A custom description explaining to the customer how to pay for the service.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z
                      .object({
                        defaultPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        minPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The minimal price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        maxPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The maximum price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                      ),
                  }),
                ])
              )
              .describe(
                'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
              )
              .optional(),
            onlineBooking: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
                  )
                  .optional()
                  .nullable(),
                requireManualApproval: z
                  .boolean()
                  .describe(
                    'Booking the service requires approval by the Wix user.'
                  )
                  .optional()
                  .nullable(),
                allowMultipleRequests: z
                  .boolean()
                  .describe(
                    'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Online booking settings.')
              .optional(),
            conferencing: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    "Whether a conference link is generated for the service's sessions."
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Conferencing options for the service.')
              .optional(),
            locations: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Location ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    type: z
                      .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                      .describe('Location type.\n\nDefault: `CUSTOM`')
                      .optional(),
                    calculatedAddress: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe(
                        'Location address. Empty for `{"type": "CUSTOMER"}`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      business: z.never().optional(),
                      custom: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      business: z
                        .object({
                          _id: z
                            .string()
                            .describe(
                              'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .string()
                            .describe('Business location name.')
                            .max(150)
                            .optional(),
                          default: z
                            .boolean()
                            .describe(
                              'Whether this is the default location. There can only be a single default location per site.'
                            )
                            .optional()
                            .nullable(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Business location address.')
                            .optional(),
                          email: z
                            .string()
                            .describe('Business location email.')
                            .email()
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Business location phone.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Information about business locations.'),
                    }),
                    z.object({
                      business: z.never().optional(),
                      custom: z
                        .object({
                          _id: z
                            .string()
                            .describe('ID of the custom location.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Address of the custom location.')
                            .optional(),
                        })
                        .describe('Information about custom locations.'),
                    }),
                  ])
                )
              )
              .max(500)
              .optional(),
            bookingPolicy: z
              .object({
                _id: z
                  .string()
                  .describe('The ID to the policy for the booking.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                _createdDate: z
                  .date()
                  .describe('Date and time the policy was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the policy was updated.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Name of the policy.')
                  .max(400)
                  .optional()
                  .nullable(),
                customPolicyDescription: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                      )
                      .optional(),
                    description: z
                      .string()
                      .describe(
                        'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                      )
                      .max(2500)
                      .optional(),
                  })
                  .describe(
                    'Custom description for the policy. This policy is displayed to the participant.'
                  )
                  .optional(),
                default: z
                  .boolean()
                  .describe(
                    'Whether the policy is the default for the meta site.'
                  )
                  .optional()
                  .nullable(),
                limitEarlyBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                      )
                      .optional(),
                    earliestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting early bookings.')
                  .optional(),
                limitLateBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting late bookings.')
                  .optional(),
                bookAfterStartPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe(
                    'Policy on booking an entity after the start of the schedule.'
                  )
                  .optional(),
                cancellationPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestCancellation: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestCancellationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for canceling a booked entity.')
                  .optional(),
                reschedulePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestReschedule: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestRescheduleInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for rescheduling a booked entity.')
                  .optional(),
                waitlistPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                      )
                      .optional(),
                    capacity: z
                      .number()
                      .int()
                      .describe(
                        'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                    reservationTimeInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Waitlist policy for the service.')
                  .optional(),
                participantsPolicy: z
                  .object({
                    maxParticipantsPerBooking: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy regarding the participants per booking.')
                  .optional(),
                resourcesPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                      )
                      .optional(),
                    autoAssignAllowed: z
                      .boolean()
                      .describe(
                        '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe('Policy for allocating resources.')
                  .optional(),
                cancellationFeePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                      )
                      .optional(),
                    cancellationWindows: z
                      .array(
                        z.intersection(
                          z.object({
                            startInMinutes: z
                              .number()
                              .int()
                              .describe(
                                'The fee will be applied if the booked session starts within this start time in minutes.'
                              )
                              .min(1)
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({
                              amount: z.never().optional(),
                              percentage: z.never().optional(),
                            }),
                            z.object({
                              percentage: z.never().optional(),
                              amount: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                    )
                                    .optional(),
                                  formattedValue: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                    )
                                    .max(50)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Amount to be charged as a cancellation fee.'
                                ),
                            }),
                            z.object({
                              amount: z.never().optional(),
                              percentage: z
                                .string()
                                .describe(
                                  'Percentage of the original price to be charged as a cancellation fee.'
                                ),
                            }),
                          ])
                        )
                      )
                      .max(2)
                      .optional(),
                    autoCollectFeeEnabled: z
                      .boolean()
                      .describe(
                        'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Rules for cancellation fees.')
                  .optional(),
                saveCreditCardPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe('Default: `false`')
                      .optional(),
                  })
                  .describe('Rule for saving credit card.')
                  .optional(),
              })
              .describe(
                '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
              )
              .optional(),
            schedule: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                firstSessionStart: z
                  .date()
                  .describe(
                    'Start time of the first session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                lastSessionEnd: z
                  .date()
                  .describe(
                    'End time of the last session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                availabilityConstraints: z
                  .object({
                    durations: z
                      .array(
                        z.object({
                          minutes: z
                            .number()
                            .int()
                            .describe(
                              'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                            )
                            .min(1)
                            .max(44639)
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                    sessionDurations: z
                      .array(z.number().int())
                      .max(50)
                      .optional(),
                    timeBetweenSessions: z
                      .number()
                      .int()
                      .describe(
                        'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                      )
                      .min(0)
                      .max(720)
                      .optional(),
                  })
                  .describe('Limitations affecting the service availability.')
                  .optional(),
              })
              .describe(
                "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
              )
              .optional(),
            staffMemberIds: z.array(z.string()).max(220).optional(),
            serviceResources: z
              .array(
                z.object({
                  resourceType: z
                    .object({
                      _id: z
                        .string()
                        .describe('The type of the resource.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('The name of the resource type.')
                        .max(40)
                        .min(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                    )
                    .optional(),
                })
              )
              .max(3)
              .optional(),
            supportedSlugs: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                    )
                    .max(500)
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the slug was created. This is a system field.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(100)
              .optional(),
            mainSlug: z
              .object({
                name: z
                  .string()
                  .describe(
                    "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                  )
                  .max(500)
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                  )
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe(
                    'Date and time the slug was created. This is a system field.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
              )
              .optional(),
            urls: z
              .object({
                servicePage: z
                  .string()
                  .describe('The URL for the service page.')
                  .optional(),
                bookingPage: z
                  .string()
                  .describe(
                    'The URL for the booking entry point. It can be either to the calendar or to the service page.'
                  )
                  .optional(),
                calendarPage: z
                  .string()
                  .describe(
                    'The URL for the calendar. Can be empty if no calendar exists.'
                  )
                  .optional(),
              })
              .describe(
                'URLs to various service-related pages, such as the calendar page and the booking page.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling users to save custom data related to the service.'
              )
              .optional(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Custom SEO data for the service.')
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
              )
              .optional()
              .nullable(),
            addOnGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Name of the add-on group.')
                    .max(100)
                    .optional()
                    .nullable(),
                  maxNumberOfAddOns: z
                    .number()
                    .int()
                    .describe(
                      "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                    )
                    .optional()
                    .nullable(),
                  addOnIds: z.array(z.string()).max(7).optional(),
                  prompt: z
                    .string()
                    .describe(
                      "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(3)
              .optional(),
            addOnDetails: z
              .array(
                z.object({
                  addOnId: z
                    .string()
                    .describe('ID of the add-on.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            taxableAddress: z
              .object({
                taxableAddressType: z
                  .enum(['BUSINESS', 'BILLING'])
                  .describe('Taxable address type.')
                  .optional(),
              })
              .describe('Taxable address used to calculate tax')
              .optional(),
          })
          .describe('Updated service.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Create statistics.')
    .optional(),
});
export const GetServiceRequest = z.object({
  serviceId: z
    .string()
    .describe('ID of the service to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetServiceResponse = z.object({
  _id: z
    .string()
    .describe('Service ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  appId: z
    .string()
    .describe(
      'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  createdByAppId: z
    .string()
    .describe(
      "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  type: z
    .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
    .describe(
      'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
    )
    .optional(),
  sortOrder: z
    .number()
    .int()
    .describe(
      'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Service name.')
    .max(400)
    .min(1)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
    )
    .max(7000)
    .optional()
    .nullable(),
  tagLine: z
    .string()
    .describe('Short service description, such as `Hair styling`.')
    .max(6000)
    .optional()
    .nullable(),
  defaultCapacity: z
    .number()
    .int()
    .describe(
      'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
    )
    .min(1)
    .max(1000)
    .optional()
    .nullable(),
  media: z
    .object({
      items: z
        .array(
          z.intersection(
            z.object({}),
            z.xor([
              z.object({ image: z.never().optional() }),
              z.object({
                image: z
                  .string()
                  .describe(
                    'Details of the image associated with the service, such as URL and size.'
                  ),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z
                .string()
                .describe(
                  'Details of the image associated with the service, such as URL and size.'
                ),
            }),
          ])
        )
        .describe('Primary media associated with the service.')
        .optional(),
      coverMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z
                .string()
                .describe(
                  'Details of the image associated with the service, such as URL and size.'
                ),
            }),
          ])
        )
        .describe('Cover media associated with the service.')
        .optional(),
    })
    .describe('Media associated with the service.')
    .optional(),
  hidden: z
    .boolean()
    .describe(
      'Whether the service is hidden from Wix Bookings pages and widgets.'
    )
    .optional()
    .nullable(),
  category: z
    .object({
      _id: z
        .string()
        .describe('Category ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z
        .string()
        .describe('Category name.')
        .max(500)
        .optional()
        .nullable(),
      sortOrder: z
        .number()
        .int()
        .describe('Order of a category within a category list.')
        .optional()
        .nullable(),
    })
    .describe(
      "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
    )
    .optional(),
  form: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe('Form the customer filled out when booking the service.')
    .optional(),
  payment: z
    .intersection(
      z.object({
        rateType: z
          .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
          .describe('The rate the customer is expected to pay for the service.')
          .optional(),
        options: z
          .object({
            online: z
              .boolean()
              .describe(
                'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
              )
              .optional()
              .nullable(),
            inPerson: z
              .boolean()
              .describe('Customers can pay for the service in person.')
              .optional()
              .nullable(),
            deposit: z
              .boolean()
              .describe(
                'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
              )
              .optional()
              .nullable(),
            pricingPlan: z
              .boolean()
              .describe(
                'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'The payment options a customer can use to pay for the service.'
          )
          .optional(),
        pricingPlanIds: z.array(z.string()).max(75).optional(),
        addOnOption: z
          .enum(['ONLINE', 'IN_PERSON'])
          .describe(
            'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          fixed: z.never().optional(),
          custom: z.never().optional(),
          varied: z.never().optional(),
        }),
        z.object({
          custom: z.never().optional(),
          varied: z.never().optional(),
          fixed: z
            .object({
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                )
                .optional(),
              deposit: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                )
                .optional(),
              fullUpfrontPaymentAllowed: z
                .boolean()
                .describe(
                  'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
            ),
        }),
        z.object({
          fixed: z.never().optional(),
          varied: z.never().optional(),
          custom: z
            .object({
              description: z
                .string()
                .describe(
                  'A custom description explaining to the customer how to pay for the service.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
            ),
        }),
        z.object({
          fixed: z.never().optional(),
          custom: z.never().optional(),
          varied: z
            .object({
              defaultPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                )
                .optional(),
              deposit: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                )
                .optional(),
              minPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The minimal price a customer may pay for this service, based on its variants.'
                )
                .optional(),
              maxPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The maximum price a customer may pay for this service, based on its variants.'
                )
                .optional(),
              fullUpfrontPaymentAllowed: z
                .boolean()
                .describe(
                  'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
            ),
        }),
      ])
    )
    .describe(
      'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
    )
    .optional(),
  onlineBooking: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
        )
        .optional()
        .nullable(),
      requireManualApproval: z
        .boolean()
        .describe('Booking the service requires approval by the Wix user.')
        .optional()
        .nullable(),
      allowMultipleRequests: z
        .boolean()
        .describe(
          'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Online booking settings.')
    .optional(),
  conferencing: z
    .object({
      enabled: z
        .boolean()
        .describe(
          "Whether a conference link is generated for the service's sessions."
        )
        .optional()
        .nullable(),
    })
    .describe('Conferencing options for the service.')
    .optional(),
  locations: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Location ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          type: z
            .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
            .describe('Location type.\n\nDefault: `CUSTOM`')
            .optional(),
          calculatedAddress: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
            })
            .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
            .optional(),
        }),
        z.xor([
          z.object({
            business: z.never().optional(),
            custom: z.never().optional(),
          }),
          z.object({
            custom: z.never().optional(),
            business: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Business location name.')
                  .max(150)
                  .optional(),
                default: z
                  .boolean()
                  .describe(
                    'Whether this is the default location. There can only be a single default location per site.'
                  )
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe('Business location address.')
                  .optional(),
                email: z
                  .string()
                  .describe('Business location email.')
                  .email()
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Business location phone.')
                  .optional()
                  .nullable(),
              })
              .describe('Information about business locations.'),
          }),
          z.object({
            business: z.never().optional(),
            custom: z
              .object({
                _id: z
                  .string()
                  .describe('ID of the custom location.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe('Address of the custom location.')
                  .optional(),
              })
              .describe('Information about custom locations.'),
          }),
        ])
      )
    )
    .max(500)
    .optional(),
  bookingPolicy: z
    .object({
      _id: z
        .string()
        .describe('The ID to the policy for the booking.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the policy was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the policy was updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Name of the policy.')
        .max(400)
        .optional()
        .nullable(),
      customPolicyDescription: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
            )
            .optional(),
          description: z
            .string()
            .describe(
              'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
            )
            .max(2500)
            .optional(),
        })
        .describe(
          'Custom description for the policy. This policy is displayed to the participant.'
        )
        .optional(),
      default: z
        .boolean()
        .describe('Whether the policy is the default for the meta site.')
        .optional()
        .nullable(),
      limitEarlyBookingPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
            )
            .optional(),
          earliestBookingInMinutes: z
            .number()
            .int()
            .describe(
              'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy for limiting early bookings.')
        .optional(),
      limitLateBookingPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
            )
            .optional(),
          latestBookingInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy for limiting late bookings.')
        .optional(),
      bookAfterStartPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Policy on booking an entity after the start of the schedule.'
        )
        .optional(),
      cancellationPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
            )
            .optional(),
          limitLatestCancellation: z
            .boolean()
            .describe(
              'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
            )
            .optional(),
          latestCancellationInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
          allowAnonymous: z
            .boolean()
            .describe(
              "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
            )
            .optional()
            .nullable(),
        })
        .describe('Policy for canceling a booked entity.')
        .optional(),
      reschedulePolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
            )
            .optional(),
          limitLatestReschedule: z
            .boolean()
            .describe(
              'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
            )
            .optional(),
          latestRescheduleInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
          allowAnonymous: z
            .boolean()
            .describe(
              "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
            )
            .optional()
            .nullable(),
        })
        .describe('Policy for rescheduling a booked entity.')
        .optional(),
      waitlistPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
            )
            .optional(),
          capacity: z
            .number()
            .int()
            .describe(
              'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
            )
            .min(1)
            .optional(),
          reservationTimeInMinutes: z
            .number()
            .int()
            .describe(
              'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
            )
            .min(1)
            .optional(),
        })
        .describe('Waitlist policy for the service.')
        .optional(),
      participantsPolicy: z
        .object({
          maxParticipantsPerBooking: z
            .number()
            .int()
            .describe(
              'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy regarding the participants per booking.')
        .optional(),
      resourcesPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
            )
            .optional(),
          autoAssignAllowed: z
            .boolean()
            .describe(
              '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe('Policy for allocating resources.')
        .optional(),
      cancellationFeePolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
            )
            .optional(),
          cancellationWindows: z
            .array(
              z.intersection(
                z.object({
                  startInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'The fee will be applied if the booked session starts within this start time in minutes.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    amount: z.never().optional(),
                    percentage: z.never().optional(),
                  }),
                  z.object({
                    percentage: z.never().optional(),
                    amount: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe('Amount to be charged as a cancellation fee.'),
                  }),
                  z.object({
                    amount: z.never().optional(),
                    percentage: z
                      .string()
                      .describe(
                        'Percentage of the original price to be charged as a cancellation fee.'
                      ),
                  }),
                ])
              )
            )
            .max(2)
            .optional(),
          autoCollectFeeEnabled: z
            .boolean()
            .describe(
              'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
            )
            .optional()
            .nullable(),
        })
        .describe('Rules for cancellation fees.')
        .optional(),
      saveCreditCardPolicy: z
        .object({
          enabled: z.boolean().describe('Default: `false`').optional(),
        })
        .describe('Rule for saving credit card.')
        .optional(),
    })
    .describe(
      '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
    )
    .optional(),
  schedule: z
    .object({
      _id: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      firstSessionStart: z
        .date()
        .describe(
          'Start time of the first session in the schedule. For courses only.'
        )
        .optional()
        .nullable(),
      lastSessionEnd: z
        .date()
        .describe(
          'End time of the last session in the schedule. For courses only.'
        )
        .optional()
        .nullable(),
      availabilityConstraints: z
        .object({
          durations: z
            .array(
              z.object({
                minutes: z
                  .number()
                  .int()
                  .describe(
                    'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                  )
                  .min(1)
                  .max(44639)
                  .optional(),
              })
            )
            .max(50)
            .optional(),
          sessionDurations: z.array(z.number().int()).max(50).optional(),
          timeBetweenSessions: z
            .number()
            .int()
            .describe(
              'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
            )
            .min(0)
            .max(720)
            .optional(),
        })
        .describe('Limitations affecting the service availability.')
        .optional(),
    })
    .describe(
      "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
    )
    .optional(),
  staffMemberIds: z.array(z.string()).max(220).optional(),
  serviceResources: z
    .array(
      z.object({
        resourceType: z
          .object({
            _id: z
              .string()
              .describe('The type of the resource.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('The name of the resource type.')
              .max(40)
              .min(1)
              .optional()
              .nullable(),
          })
          .describe(
            'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
          )
          .optional(),
      })
    )
    .max(3)
    .optional(),
  supportedSlugs: z
    .array(
      z.object({
        name: z
          .string()
          .describe(
            "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
          )
          .max(500)
          .optional(),
        custom: z
          .boolean()
          .describe(
            'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the slug was created. This is a system field.'
          )
          .optional()
          .nullable(),
      })
    )
    .max(100)
    .optional(),
  mainSlug: z
    .object({
      name: z
        .string()
        .describe(
          "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
        )
        .max(500)
        .optional(),
      custom: z
        .boolean()
        .describe(
          'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the slug was created. This is a system field.')
        .optional()
        .nullable(),
    })
    .describe(
      'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
    )
    .optional(),
  urls: z
    .object({
      servicePage: z
        .string()
        .describe('The URL for the service page.')
        .optional(),
      bookingPage: z
        .string()
        .describe(
          'The URL for the booking entry point. It can be either to the calendar or to the service page.'
        )
        .optional(),
      calendarPage: z
        .string()
        .describe(
          'The URL for the calendar. Can be empty if no calendar exists.'
        )
        .optional(),
    })
    .describe(
      'URLs to various service-related pages, such as the calendar page and the booking page.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling users to save custom data related to the service.'
    )
    .optional(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('Custom SEO data for the service.')
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
    )
    .optional()
    .nullable(),
  addOnGroups: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of the add-on group.')
          .max(100)
          .optional()
          .nullable(),
        maxNumberOfAddOns: z
          .number()
          .int()
          .describe(
            "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
          )
          .optional()
          .nullable(),
        addOnIds: z.array(z.string()).max(7).optional(),
        prompt: z
          .string()
          .describe(
            "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
          )
          .max(200)
          .optional()
          .nullable(),
      })
    )
    .max(3)
    .optional(),
  addOnDetails: z
    .array(
      z.object({
        addOnId: z
          .string()
          .describe('ID of the add-on.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        durationInMinutes: z
          .number()
          .int()
          .describe(
            'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
          )
          .optional()
          .nullable(),
      })
    )
    .max(50)
    .optional(),
  taxableAddress: z
    .object({
      taxableAddressType: z
        .enum(['BUSINESS', 'BILLING'])
        .describe('Taxable address type.')
        .optional(),
    })
    .describe('Taxable address used to calculate tax')
    .optional(),
});
export const UpdateServiceRequest = z.object({
  _id: z
    .string()
    .describe('Service ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z.enum(['APPOINTMENT', 'CLASS', 'COURSE']).optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z.enum(['ONLINE', 'IN_PERSON']).optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z.enum(['CUSTOM', 'BUSINESS', 'CUSTOMER']).optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        ),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z.enum(['BUSINESS', 'BILLING']).optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('Service to update.'),
});
export const UpdateServiceResponse = z.object({
  _id: z
    .string()
    .describe('Service ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  appId: z
    .string()
    .describe(
      'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  createdByAppId: z
    .string()
    .describe(
      "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  type: z
    .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
    .describe(
      'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
    )
    .optional(),
  sortOrder: z
    .number()
    .int()
    .describe(
      'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Service name.')
    .max(400)
    .min(1)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
    )
    .max(7000)
    .optional()
    .nullable(),
  tagLine: z
    .string()
    .describe('Short service description, such as `Hair styling`.')
    .max(6000)
    .optional()
    .nullable(),
  defaultCapacity: z
    .number()
    .int()
    .describe(
      'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
    )
    .min(1)
    .max(1000)
    .optional()
    .nullable(),
  media: z
    .object({
      items: z
        .array(
          z.intersection(
            z.object({}),
            z.xor([
              z.object({ image: z.never().optional() }),
              z.object({
                image: z
                  .string()
                  .describe(
                    'Details of the image associated with the service, such as URL and size.'
                  ),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z
                .string()
                .describe(
                  'Details of the image associated with the service, such as URL and size.'
                ),
            }),
          ])
        )
        .describe('Primary media associated with the service.')
        .optional(),
      coverMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z
                .string()
                .describe(
                  'Details of the image associated with the service, such as URL and size.'
                ),
            }),
          ])
        )
        .describe('Cover media associated with the service.')
        .optional(),
    })
    .describe('Media associated with the service.')
    .optional(),
  hidden: z
    .boolean()
    .describe(
      'Whether the service is hidden from Wix Bookings pages and widgets.'
    )
    .optional()
    .nullable(),
  category: z
    .object({
      _id: z
        .string()
        .describe('Category ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z
        .string()
        .describe('Category name.')
        .max(500)
        .optional()
        .nullable(),
      sortOrder: z
        .number()
        .int()
        .describe('Order of a category within a category list.')
        .optional()
        .nullable(),
    })
    .describe(
      "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
    )
    .optional(),
  form: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe('Form the customer filled out when booking the service.')
    .optional(),
  payment: z
    .intersection(
      z.object({
        rateType: z
          .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
          .describe('The rate the customer is expected to pay for the service.')
          .optional(),
        options: z
          .object({
            online: z
              .boolean()
              .describe(
                'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
              )
              .optional()
              .nullable(),
            inPerson: z
              .boolean()
              .describe('Customers can pay for the service in person.')
              .optional()
              .nullable(),
            deposit: z
              .boolean()
              .describe(
                'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
              )
              .optional()
              .nullable(),
            pricingPlan: z
              .boolean()
              .describe(
                'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'The payment options a customer can use to pay for the service.'
          )
          .optional(),
        pricingPlanIds: z.array(z.string()).max(75).optional(),
        addOnOption: z
          .enum(['ONLINE', 'IN_PERSON'])
          .describe(
            'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          fixed: z.never().optional(),
          custom: z.never().optional(),
          varied: z.never().optional(),
        }),
        z.object({
          custom: z.never().optional(),
          varied: z.never().optional(),
          fixed: z
            .object({
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                )
                .optional(),
              deposit: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                )
                .optional(),
              fullUpfrontPaymentAllowed: z
                .boolean()
                .describe(
                  'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
            ),
        }),
        z.object({
          fixed: z.never().optional(),
          varied: z.never().optional(),
          custom: z
            .object({
              description: z
                .string()
                .describe(
                  'A custom description explaining to the customer how to pay for the service.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
            ),
        }),
        z.object({
          fixed: z.never().optional(),
          custom: z.never().optional(),
          varied: z
            .object({
              defaultPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                )
                .optional(),
              deposit: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                )
                .optional(),
              minPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The minimal price a customer may pay for this service, based on its variants.'
                )
                .optional(),
              maxPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The maximum price a customer may pay for this service, based on its variants.'
                )
                .optional(),
              fullUpfrontPaymentAllowed: z
                .boolean()
                .describe(
                  'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
            ),
        }),
      ])
    )
    .describe(
      'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
    )
    .optional(),
  onlineBooking: z
    .object({
      enabled: z
        .boolean()
        .describe(
          'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
        )
        .optional()
        .nullable(),
      requireManualApproval: z
        .boolean()
        .describe('Booking the service requires approval by the Wix user.')
        .optional()
        .nullable(),
      allowMultipleRequests: z
        .boolean()
        .describe(
          'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Online booking settings.')
    .optional(),
  conferencing: z
    .object({
      enabled: z
        .boolean()
        .describe(
          "Whether a conference link is generated for the service's sessions."
        )
        .optional()
        .nullable(),
    })
    .describe('Conferencing options for the service.')
    .optional(),
  locations: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Location ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          type: z
            .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
            .describe('Location type.\n\nDefault: `CUSTOM`')
            .optional(),
          calculatedAddress: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
            })
            .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
            .optional(),
        }),
        z.xor([
          z.object({
            business: z.never().optional(),
            custom: z.never().optional(),
          }),
          z.object({
            custom: z.never().optional(),
            business: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Business location name.')
                  .max(150)
                  .optional(),
                default: z
                  .boolean()
                  .describe(
                    'Whether this is the default location. There can only be a single default location per site.'
                  )
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe('Business location address.')
                  .optional(),
                email: z
                  .string()
                  .describe('Business location email.')
                  .email()
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Business location phone.')
                  .optional()
                  .nullable(),
              })
              .describe('Information about business locations.'),
          }),
          z.object({
            business: z.never().optional(),
            custom: z
              .object({
                _id: z
                  .string()
                  .describe('ID of the custom location.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe('Address of the custom location.')
                  .optional(),
              })
              .describe('Information about custom locations.'),
          }),
        ])
      )
    )
    .max(500)
    .optional(),
  bookingPolicy: z
    .object({
      _id: z
        .string()
        .describe('The ID to the policy for the booking.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the policy was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the policy was updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Name of the policy.')
        .max(400)
        .optional()
        .nullable(),
      customPolicyDescription: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
            )
            .optional(),
          description: z
            .string()
            .describe(
              'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
            )
            .max(2500)
            .optional(),
        })
        .describe(
          'Custom description for the policy. This policy is displayed to the participant.'
        )
        .optional(),
      default: z
        .boolean()
        .describe('Whether the policy is the default for the meta site.')
        .optional()
        .nullable(),
      limitEarlyBookingPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
            )
            .optional(),
          earliestBookingInMinutes: z
            .number()
            .int()
            .describe(
              'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy for limiting early bookings.')
        .optional(),
      limitLateBookingPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
            )
            .optional(),
          latestBookingInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy for limiting late bookings.')
        .optional(),
      bookAfterStartPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe(
          'Policy on booking an entity after the start of the schedule.'
        )
        .optional(),
      cancellationPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
            )
            .optional(),
          limitLatestCancellation: z
            .boolean()
            .describe(
              'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
            )
            .optional(),
          latestCancellationInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
          allowAnonymous: z
            .boolean()
            .describe(
              "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
            )
            .optional()
            .nullable(),
        })
        .describe('Policy for canceling a booked entity.')
        .optional(),
      reschedulePolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
            )
            .optional(),
          limitLatestReschedule: z
            .boolean()
            .describe(
              'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
            )
            .optional(),
          latestRescheduleInMinutes: z
            .number()
            .int()
            .describe(
              'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
            )
            .min(1)
            .optional(),
          allowAnonymous: z
            .boolean()
            .describe(
              "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
            )
            .optional()
            .nullable(),
        })
        .describe('Policy for rescheduling a booked entity.')
        .optional(),
      waitlistPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
            )
            .optional(),
          capacity: z
            .number()
            .int()
            .describe(
              'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
            )
            .min(1)
            .optional(),
          reservationTimeInMinutes: z
            .number()
            .int()
            .describe(
              'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
            )
            .min(1)
            .optional(),
        })
        .describe('Waitlist policy for the service.')
        .optional(),
      participantsPolicy: z
        .object({
          maxParticipantsPerBooking: z
            .number()
            .int()
            .describe(
              'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
            )
            .min(1)
            .optional(),
        })
        .describe('Policy regarding the participants per booking.')
        .optional(),
      resourcesPolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
            )
            .optional(),
          autoAssignAllowed: z
            .boolean()
            .describe(
              '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
            )
            .optional(),
        })
        .describe('Policy for allocating resources.')
        .optional(),
      cancellationFeePolicy: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
            )
            .optional(),
          cancellationWindows: z
            .array(
              z.intersection(
                z.object({
                  startInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'The fee will be applied if the booked session starts within this start time in minutes.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    amount: z.never().optional(),
                    percentage: z.never().optional(),
                  }),
                  z.object({
                    percentage: z.never().optional(),
                    amount: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe('Amount to be charged as a cancellation fee.'),
                  }),
                  z.object({
                    amount: z.never().optional(),
                    percentage: z
                      .string()
                      .describe(
                        'Percentage of the original price to be charged as a cancellation fee.'
                      ),
                  }),
                ])
              )
            )
            .max(2)
            .optional(),
          autoCollectFeeEnabled: z
            .boolean()
            .describe(
              'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
            )
            .optional()
            .nullable(),
        })
        .describe('Rules for cancellation fees.')
        .optional(),
      saveCreditCardPolicy: z
        .object({
          enabled: z.boolean().describe('Default: `false`').optional(),
        })
        .describe('Rule for saving credit card.')
        .optional(),
    })
    .describe(
      '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
    )
    .optional(),
  schedule: z
    .object({
      _id: z
        .string()
        .describe(
          "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      firstSessionStart: z
        .date()
        .describe(
          'Start time of the first session in the schedule. For courses only.'
        )
        .optional()
        .nullable(),
      lastSessionEnd: z
        .date()
        .describe(
          'End time of the last session in the schedule. For courses only.'
        )
        .optional()
        .nullable(),
      availabilityConstraints: z
        .object({
          durations: z
            .array(
              z.object({
                minutes: z
                  .number()
                  .int()
                  .describe(
                    'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                  )
                  .min(1)
                  .max(44639)
                  .optional(),
              })
            )
            .max(50)
            .optional(),
          sessionDurations: z.array(z.number().int()).max(50).optional(),
          timeBetweenSessions: z
            .number()
            .int()
            .describe(
              'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
            )
            .min(0)
            .max(720)
            .optional(),
        })
        .describe('Limitations affecting the service availability.')
        .optional(),
    })
    .describe(
      "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
    )
    .optional(),
  staffMemberIds: z.array(z.string()).max(220).optional(),
  serviceResources: z
    .array(
      z.object({
        resourceType: z
          .object({
            _id: z
              .string()
              .describe('The type of the resource.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('The name of the resource type.')
              .max(40)
              .min(1)
              .optional()
              .nullable(),
          })
          .describe(
            'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
          )
          .optional(),
      })
    )
    .max(3)
    .optional(),
  supportedSlugs: z
    .array(
      z.object({
        name: z
          .string()
          .describe(
            "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
          )
          .max(500)
          .optional(),
        custom: z
          .boolean()
          .describe(
            'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the slug was created. This is a system field.'
          )
          .optional()
          .nullable(),
      })
    )
    .max(100)
    .optional(),
  mainSlug: z
    .object({
      name: z
        .string()
        .describe(
          "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
        )
        .max(500)
        .optional(),
      custom: z
        .boolean()
        .describe(
          'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the slug was created. This is a system field.')
        .optional()
        .nullable(),
    })
    .describe(
      'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
    )
    .optional(),
  urls: z
    .object({
      servicePage: z
        .string()
        .describe('The URL for the service page.')
        .optional(),
      bookingPage: z
        .string()
        .describe(
          'The URL for the booking entry point. It can be either to the calendar or to the service page.'
        )
        .optional(),
      calendarPage: z
        .string()
        .describe(
          'The URL for the calendar. Can be empty if no calendar exists.'
        )
        .optional(),
    })
    .describe(
      'URLs to various service-related pages, such as the calendar page and the booking page.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling users to save custom data related to the service.'
    )
    .optional(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('Custom SEO data for the service.')
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
    )
    .optional()
    .nullable(),
  addOnGroups: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of the add-on group.')
          .max(100)
          .optional()
          .nullable(),
        maxNumberOfAddOns: z
          .number()
          .int()
          .describe(
            "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
          )
          .optional()
          .nullable(),
        addOnIds: z.array(z.string()).max(7).optional(),
        prompt: z
          .string()
          .describe(
            "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
          )
          .max(200)
          .optional()
          .nullable(),
      })
    )
    .max(3)
    .optional(),
  addOnDetails: z
    .array(
      z.object({
        addOnId: z
          .string()
          .describe('ID of the add-on.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        durationInMinutes: z
          .number()
          .int()
          .describe(
            'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
          )
          .optional()
          .nullable(),
      })
    )
    .max(50)
    .optional(),
  taxableAddress: z
    .object({
      taxableAddressType: z
        .enum(['BUSINESS', 'BILLING'])
        .describe('Taxable address type.')
        .optional(),
    })
    .describe('Taxable address used to calculate tax')
    .optional(),
});
export const BulkUpdateServicesRequest = z.object({
  options: z
    .object({
      services: z
        .array(
          z.object({
            service: z
              .object({
                _id: z
                  .string()
                  .describe('Service ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
                appId: z
                  .string()
                  .describe(
                    'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                createdByAppId: z
                  .string()
                  .describe(
                    "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                type: z.enum(['APPOINTMENT', 'CLASS', 'COURSE']).optional(),
                sortOrder: z
                  .number()
                  .int()
                  .describe(
                    'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Service name.')
                  .max(400)
                  .min(1)
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe(
                    'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
                  )
                  .max(7000)
                  .optional()
                  .nullable(),
                tagLine: z
                  .string()
                  .describe(
                    'Short service description, such as `Hair styling`.'
                  )
                  .max(6000)
                  .optional()
                  .nullable(),
                defaultCapacity: z
                  .number()
                  .int()
                  .describe(
                    'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
                  )
                  .min(1)
                  .max(1000)
                  .optional()
                  .nullable(),
                media: z
                  .object({
                    items: z
                      .array(
                        z.intersection(
                          z.object({}),
                          z.xor([
                            z.object({ image: z.never().optional() }),
                            z.object({
                              image: z
                                .string()
                                .describe(
                                  'Details of the image associated with the service, such as URL and size.'
                                ),
                            }),
                          ])
                        )
                      )
                      .max(100)
                      .optional(),
                    mainMedia: z
                      .intersection(
                        z.object({}),
                        z.xor([
                          z.object({ image: z.never().optional() }),
                          z.object({
                            image: z
                              .string()
                              .describe(
                                'Details of the image associated with the service, such as URL and size.'
                              ),
                          }),
                        ])
                      )
                      .describe('Primary media associated with the service.')
                      .optional(),
                    coverMedia: z
                      .intersection(
                        z.object({}),
                        z.xor([
                          z.object({ image: z.never().optional() }),
                          z.object({
                            image: z
                              .string()
                              .describe(
                                'Details of the image associated with the service, such as URL and size.'
                              ),
                          }),
                        ])
                      )
                      .describe('Cover media associated with the service.')
                      .optional(),
                  })
                  .describe('Media associated with the service.')
                  .optional(),
                hidden: z
                  .boolean()
                  .describe(
                    'Whether the service is hidden from Wix Bookings pages and widgets.'
                  )
                  .optional()
                  .nullable(),
                category: z
                  .object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Category name.')
                      .max(500)
                      .optional()
                      .nullable(),
                    sortOrder: z
                      .number()
                      .int()
                      .describe('Order of a category within a category list.')
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
                  )
                  .optional(),
                form: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                  .describe(
                    'Form the customer filled out when booking the service.'
                  )
                  .optional(),
                payment: z
                  .intersection(
                    z.object({
                      rateType: z
                        .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                        .optional(),
                      options: z
                        .object({
                          online: z
                            .boolean()
                            .describe(
                              'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                            )
                            .optional()
                            .nullable(),
                          inPerson: z
                            .boolean()
                            .describe(
                              'Customers can pay for the service in person.'
                            )
                            .optional()
                            .nullable(),
                          deposit: z
                            .boolean()
                            .describe(
                              'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                            )
                            .optional()
                            .nullable(),
                          pricingPlan: z
                            .boolean()
                            .describe(
                              'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'The payment options a customer can use to pay for the service.'
                        )
                        .optional(),
                      pricingPlanIds: z.array(z.string()).max(75).optional(),
                      addOnOption: z.enum(['ONLINE', 'IN_PERSON']).optional(),
                    }),
                    z.xor([
                      z.object({
                        fixed: z.never().optional(),
                        custom: z.never().optional(),
                        varied: z.never().optional(),
                      }),
                      z.object({
                        custom: z.never().optional(),
                        varied: z.never().optional(),
                        fixed: z
                          .object({
                            price: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                  )
                                  .optional(),
                                currency: z
                                  .string()
                                  .describe(
                                    'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                  )
                                  .optional(),
                                formattedValue: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                  )
                                  .max(50)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                              )
                              .optional(),
                            deposit: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                  )
                                  .optional(),
                                currency: z
                                  .string()
                                  .describe(
                                    'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                  )
                                  .optional(),
                                formattedValue: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                  )
                                  .max(50)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                              )
                              .optional(),
                            fullUpfrontPaymentAllowed: z
                              .boolean()
                              .describe(
                                'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                          ),
                      }),
                      z.object({
                        fixed: z.never().optional(),
                        varied: z.never().optional(),
                        custom: z
                          .object({
                            description: z
                              .string()
                              .describe(
                                'A custom description explaining to the customer how to pay for the service.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                          ),
                      }),
                      z.object({
                        fixed: z.never().optional(),
                        custom: z.never().optional(),
                        varied: z
                          .object({
                            defaultPrice: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                  )
                                  .optional(),
                                currency: z
                                  .string()
                                  .describe(
                                    'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                  )
                                  .optional(),
                                formattedValue: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                  )
                                  .max(50)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                              )
                              .optional(),
                            deposit: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                  )
                                  .optional(),
                                currency: z
                                  .string()
                                  .describe(
                                    'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                  )
                                  .optional(),
                                formattedValue: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                  )
                                  .max(50)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                              )
                              .optional(),
                            minPrice: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                  )
                                  .optional(),
                                currency: z
                                  .string()
                                  .describe(
                                    'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                  )
                                  .optional(),
                                formattedValue: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                  )
                                  .max(50)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'The minimal price a customer may pay for this service, based on its variants.'
                              )
                              .optional(),
                            maxPrice: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                  )
                                  .optional(),
                                currency: z
                                  .string()
                                  .describe(
                                    'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                  )
                                  .optional(),
                                formattedValue: z
                                  .string()
                                  .describe(
                                    'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                  )
                                  .max(50)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'The maximum price a customer may pay for this service, based on its variants.'
                              )
                              .optional(),
                            fullUpfrontPaymentAllowed: z
                              .boolean()
                              .describe(
                                'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                          ),
                      }),
                    ])
                  )
                  .describe(
                    'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional(),
                onlineBooking: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
                      )
                      .optional()
                      .nullable(),
                    requireManualApproval: z
                      .boolean()
                      .describe(
                        'Booking the service requires approval by the Wix user.'
                      )
                      .optional()
                      .nullable(),
                    allowMultipleRequests: z
                      .boolean()
                      .describe(
                        'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Online booking settings.')
                  .optional(),
                conferencing: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        "Whether a conference link is generated for the service's sessions."
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Conferencing options for the service.')
                  .optional(),
                locations: z
                  .array(
                    z.intersection(
                      z.object({
                        _id: z
                          .string()
                          .describe('Location ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        type: z
                          .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                          .optional(),
                        calculatedAddress: z
                          .object({
                            city: z.string().optional().nullable(),
                            subdivision: z.string().optional().nullable(),
                            country: z.string().optional().nullable(),
                            postalCode: z.string().optional().nullable(),
                            addressLine1: z.string().optional().nullable(),
                          })
                          .describe(
                            'Location address. Empty for `{"type": "CUSTOMER"}`.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          business: z.never().optional(),
                          custom: z.never().optional(),
                        }),
                        z.object({
                          custom: z.never().optional(),
                          business: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Business location name.')
                                .max(150)
                                .optional(),
                              default: z
                                .boolean()
                                .describe(
                                  'Whether this is the default location. There can only be a single default location per site.'
                                )
                                .optional()
                                .nullable(),
                              address: z
                                .object({
                                  city: z.string().optional().nullable(),
                                  subdivision: z.string().optional().nullable(),
                                  country: z.string().optional().nullable(),
                                  postalCode: z.string().optional().nullable(),
                                  addressLine1: z
                                    .string()
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Business location address.')
                                .optional(),
                              email: z
                                .string()
                                .describe('Business location email.')
                                .email()
                                .optional()
                                .nullable(),
                              phone: z
                                .string()
                                .describe('Business location phone.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Information about business locations.'),
                        }),
                        z.object({
                          business: z.never().optional(),
                          custom: z
                            .object({
                              _id: z
                                .string()
                                .describe('ID of the custom location.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              address: z
                                .object({
                                  city: z.string().optional().nullable(),
                                  subdivision: z.string().optional().nullable(),
                                  country: z.string().optional().nullable(),
                                  postalCode: z.string().optional().nullable(),
                                  addressLine1: z
                                    .string()
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Address of the custom location.')
                                .optional(),
                            })
                            .describe('Information about custom locations.'),
                        }),
                      ])
                    )
                  )
                  .max(500)
                  .optional(),
                bookingPolicy: z
                  .object({
                    _id: z
                      .string()
                      .describe('The ID to the policy for the booking.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    _createdDate: z
                      .date()
                      .describe('Date and time the policy was created.')
                      .optional()
                      .nullable(),
                    _updatedDate: z
                      .date()
                      .describe('Date and time the policy was updated.')
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Name of the policy.')
                      .max(400)
                      .optional()
                      .nullable(),
                    customPolicyDescription: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                          )
                          .max(2500)
                          .optional(),
                      })
                      .describe(
                        'Custom description for the policy. This policy is displayed to the participant.'
                      )
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether the policy is the default for the meta site.'
                      )
                      .optional()
                      .nullable(),
                    limitEarlyBookingPolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                          )
                          .optional(),
                        earliestBookingInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                          )
                          .min(1)
                          .optional(),
                      })
                      .describe('Policy for limiting early bookings.')
                      .optional(),
                    limitLateBookingPolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                          )
                          .optional(),
                        latestBookingInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                          )
                          .min(1)
                          .optional(),
                      })
                      .describe('Policy for limiting late bookings.')
                      .optional(),
                    bookAfterStartPolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                          )
                          .optional(),
                      })
                      .describe(
                        'Policy on booking an entity after the start of the schedule.'
                      )
                      .optional(),
                    cancellationPolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                          )
                          .optional(),
                        limitLatestCancellation: z
                          .boolean()
                          .describe(
                            'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                          )
                          .optional(),
                        latestCancellationInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                          )
                          .min(1)
                          .optional(),
                        allowAnonymous: z
                          .boolean()
                          .describe(
                            "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Policy for canceling a booked entity.')
                      .optional(),
                    reschedulePolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                          )
                          .optional(),
                        limitLatestReschedule: z
                          .boolean()
                          .describe(
                            'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                          )
                          .optional(),
                        latestRescheduleInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                          )
                          .min(1)
                          .optional(),
                        allowAnonymous: z
                          .boolean()
                          .describe(
                            "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Policy for rescheduling a booked entity.')
                      .optional(),
                    waitlistPolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                          )
                          .optional(),
                        capacity: z
                          .number()
                          .int()
                          .describe(
                            'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                          )
                          .min(1)
                          .optional(),
                        reservationTimeInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                          )
                          .min(1)
                          .optional(),
                      })
                      .describe('Waitlist policy for the service.')
                      .optional(),
                    participantsPolicy: z
                      .object({
                        maxParticipantsPerBooking: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                          )
                          .min(1)
                          .optional(),
                      })
                      .describe(
                        'Policy regarding the participants per booking.'
                      )
                      .optional(),
                    resourcesPolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                          )
                          .optional(),
                        autoAssignAllowed: z
                          .boolean()
                          .describe(
                            '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                          )
                          .optional(),
                      })
                      .describe('Policy for allocating resources.')
                      .optional(),
                    cancellationFeePolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                          )
                          .optional(),
                        cancellationWindows: z
                          .array(
                            z.intersection(
                              z.object({
                                startInMinutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'The fee will be applied if the booked session starts within this start time in minutes.'
                                  )
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              }),
                              z.xor([
                                z.object({
                                  amount: z.never().optional(),
                                  percentage: z.never().optional(),
                                }),
                                z.object({
                                  percentage: z.never().optional(),
                                  amount: z
                                    .object({
                                      value: z
                                        .string()
                                        .describe(
                                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                        )
                                        .optional(),
                                      currency: z
                                        .string()
                                        .describe(
                                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                        )
                                        .optional(),
                                      formattedValue: z
                                        .string()
                                        .describe(
                                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                        )
                                        .max(50)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Amount to be charged as a cancellation fee.'
                                    ),
                                }),
                                z.object({
                                  amount: z.never().optional(),
                                  percentage: z
                                    .string()
                                    .describe(
                                      'Percentage of the original price to be charged as a cancellation fee.'
                                    ),
                                }),
                              ])
                            )
                          )
                          .max(2)
                          .optional(),
                        autoCollectFeeEnabled: z
                          .boolean()
                          .describe(
                            'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Rules for cancellation fees.')
                      .optional(),
                    saveCreditCardPolicy: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe('Default: `false`')
                          .optional(),
                      })
                      .describe('Rule for saving credit card.')
                      .optional(),
                  })
                  .describe(
                    '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
                  )
                  .optional(),
                schedule: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .date()
                      .describe(
                        'Start time of the first session in the schedule. For courses only.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .date()
                      .describe(
                        'End time of the last session in the schedule. For courses only.'
                      )
                      .optional()
                      .nullable(),
                    availabilityConstraints: z
                      .object({
                        durations: z
                          .array(
                            z.object({
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                                )
                                .min(1)
                                .max(44639)
                                .optional(),
                            })
                          )
                          .max(50)
                          .optional(),
                        sessionDurations: z
                          .array(z.number().int())
                          .max(50)
                          .optional(),
                        timeBetweenSessions: z
                          .number()
                          .int()
                          .describe(
                            'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                          )
                          .min(0)
                          .max(720)
                          .optional(),
                      })
                      .describe(
                        'Limitations affecting the service availability.'
                      )
                      .optional(),
                  })
                  .describe(
                    "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
                  )
                  .optional(),
                staffMemberIds: z.array(z.string()).max(220).optional(),
                serviceResources: z
                  .array(
                    z.object({
                      resourceType: z
                        .object({
                          _id: z
                            .string()
                            .describe('The type of the resource.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('The name of the resource type.')
                            .max(40)
                            .min(1)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                        )
                        .optional(),
                    })
                  )
                  .max(3)
                  .optional(),
                supportedSlugs: z
                  .array(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                        )
                        .max(500)
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                        )
                        .optional()
                        .nullable(),
                      _createdDate: z
                        .date()
                        .describe(
                          'Date and time the slug was created. This is a system field.'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(100)
                  .optional(),
                mainSlug: z
                  .object({
                    name: z
                      .string()
                      .describe(
                        "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                      )
                      .max(500)
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                      )
                      .optional()
                      .nullable(),
                    _createdDate: z
                      .date()
                      .describe(
                        'Date and time the slug was created. This is a system field.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
                  )
                  .optional(),
                urls: z
                  .object({
                    servicePage: z
                      .string()
                      .describe('The URL for the service page.')
                      .optional(),
                    bookingPage: z
                      .string()
                      .describe(
                        'The URL for the booking entry point. It can be either to the calendar or to the service page.'
                      )
                      .optional(),
                    calendarPage: z
                      .string()
                      .describe(
                        'The URL for the calendar. Can be empty if no calendar exists.'
                      )
                      .optional(),
                  })
                  .describe(
                    'URLs to various service-related pages, such as the calendar page and the booking page.'
                  )
                  .optional(),
                extendedFields: z
                  .object({
                    namespaces: z
                      .record(z.string(), z.record(z.string(), z.any()))
                      .describe(
                        'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Extensions enabling users to save custom data related to the service.'
                  )
                  .optional(),
                seoData: z
                  .object({
                    tags: z
                      .array(
                        z.object({
                          type: z
                            .string()
                            .describe(
                              'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                            )
                            .optional(),
                          props: z
                            .record(z.string(), z.any())
                            .describe(
                              'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                            )
                            .optional()
                            .nullable(),
                          meta: z
                            .record(z.string(), z.any())
                            .describe(
                              'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                            )
                            .optional()
                            .nullable(),
                          children: z
                            .string()
                            .describe(
                              'SEO tag inner content. For example, `<title> inner content </title>`.'
                            )
                            .optional(),
                          custom: z
                            .boolean()
                            .describe(
                              'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                            )
                            .optional(),
                          disabled: z
                            .boolean()
                            .describe(
                              "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    settings: z
                      .object({
                        preventAutoRedirect: z
                          .boolean()
                          .describe(
                            'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                          )
                          .optional(),
                        keywords: z
                          .array(
                            z.object({
                              term: z
                                .string()
                                .describe('Keyword value.')
                                .optional(),
                              isMain: z
                                .boolean()
                                .describe(
                                  'Whether the keyword is the main focus keyword.'
                                )
                                .optional(),
                              origin: z
                                .string()
                                .describe(
                                  'The source that added the keyword terms to the SEO settings.'
                                )
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                          )
                          .max(5)
                          .optional(),
                      })
                      .describe('SEO general settings.')
                      .optional(),
                  })
                  .describe('Custom SEO data for the service.')
                  .optional(),
                _createdDate: z
                  .date()
                  .describe(
                    'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                  )
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe(
                    'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                  )
                  .optional()
                  .nullable(),
                revision: z
                  .string()
                  .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                  .describe(
                    'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
                  ),
                addOnGroups: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Name of the add-on group.')
                        .max(100)
                        .optional()
                        .nullable(),
                      maxNumberOfAddOns: z
                        .number()
                        .int()
                        .describe(
                          "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                        )
                        .optional()
                        .nullable(),
                      addOnIds: z.array(z.string()).max(7).optional(),
                      prompt: z
                        .string()
                        .describe(
                          "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(3)
                  .optional(),
                addOnDetails: z
                  .array(
                    z.object({
                      addOnId: z
                        .string()
                        .describe('ID of the add-on.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      durationInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
                taxableAddress: z
                  .object({
                    taxableAddressType: z
                      .enum(['BUSINESS', 'BILLING'])
                      .optional(),
                  })
                  .describe('Taxable address used to calculate tax')
                  .optional(),
              })
              .describe('Service to update.'),
            mask: z.array(z.string()).optional(),
          })
        )
        .max(100)
        .optional(),
      returnEntity: z
        .boolean()
        .describe(
          'Whether to include the updated services in the response. Default: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateServicesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Update metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Service ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe(
                'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            createdByAppId: z
              .string()
              .describe(
                "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
              .describe(
                'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
              )
              .optional(),
            sortOrder: z
              .number()
              .int()
              .describe(
                'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Service name.')
              .max(400)
              .min(1)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe(
                'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
              )
              .max(7000)
              .optional()
              .nullable(),
            tagLine: z
              .string()
              .describe('Short service description, such as `Hair styling`.')
              .max(6000)
              .optional()
              .nullable(),
            defaultCapacity: z
              .number()
              .int()
              .describe(
                'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
              )
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
            media: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({ image: z.never().optional() }),
                        z.object({
                          image: z
                            .string()
                            .describe(
                              'Details of the image associated with the service, such as URL and size.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                mainMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Primary media associated with the service.')
                  .optional(),
                coverMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Cover media associated with the service.')
                  .optional(),
              })
              .describe('Media associated with the service.')
              .optional(),
            hidden: z
              .boolean()
              .describe(
                'Whether the service is hidden from Wix Bookings pages and widgets.'
              )
              .optional()
              .nullable(),
            category: z
              .object({
                _id: z
                  .string()
                  .describe('Category ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Category name.')
                  .max(500)
                  .optional()
                  .nullable(),
                sortOrder: z
                  .number()
                  .int()
                  .describe('Order of a category within a category list.')
                  .optional()
                  .nullable(),
              })
              .describe(
                "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
              )
              .optional(),
            form: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Form the customer filled out when booking the service.'
              )
              .optional(),
            payment: z
              .intersection(
                z.object({
                  rateType: z
                    .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                    .describe(
                      'The rate the customer is expected to pay for the service.'
                    )
                    .optional(),
                  options: z
                    .object({
                      online: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                        )
                        .optional()
                        .nullable(),
                      inPerson: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service in person.'
                        )
                        .optional()
                        .nullable(),
                      deposit: z
                        .boolean()
                        .describe(
                          'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                        )
                        .optional()
                        .nullable(),
                      pricingPlan: z
                        .boolean()
                        .describe(
                          'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The payment options a customer can use to pay for the service.'
                    )
                    .optional(),
                  pricingPlanIds: z.array(z.string()).max(75).optional(),
                  addOnOption: z
                    .enum(['ONLINE', 'IN_PERSON'])
                    .describe(
                      'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                    fixed: z
                      .object({
                        price: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    varied: z.never().optional(),
                    custom: z
                      .object({
                        description: z
                          .string()
                          .describe(
                            'A custom description explaining to the customer how to pay for the service.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z
                      .object({
                        defaultPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        minPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The minimal price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        maxPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The maximum price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                      ),
                  }),
                ])
              )
              .describe(
                'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
              )
              .optional(),
            onlineBooking: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
                  )
                  .optional()
                  .nullable(),
                requireManualApproval: z
                  .boolean()
                  .describe(
                    'Booking the service requires approval by the Wix user.'
                  )
                  .optional()
                  .nullable(),
                allowMultipleRequests: z
                  .boolean()
                  .describe(
                    'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Online booking settings.')
              .optional(),
            conferencing: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    "Whether a conference link is generated for the service's sessions."
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Conferencing options for the service.')
              .optional(),
            locations: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Location ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    type: z
                      .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                      .describe('Location type.\n\nDefault: `CUSTOM`')
                      .optional(),
                    calculatedAddress: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe(
                        'Location address. Empty for `{"type": "CUSTOMER"}`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      business: z.never().optional(),
                      custom: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      business: z
                        .object({
                          _id: z
                            .string()
                            .describe(
                              'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .string()
                            .describe('Business location name.')
                            .max(150)
                            .optional(),
                          default: z
                            .boolean()
                            .describe(
                              'Whether this is the default location. There can only be a single default location per site.'
                            )
                            .optional()
                            .nullable(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Business location address.')
                            .optional(),
                          email: z
                            .string()
                            .describe('Business location email.')
                            .email()
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Business location phone.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Information about business locations.'),
                    }),
                    z.object({
                      business: z.never().optional(),
                      custom: z
                        .object({
                          _id: z
                            .string()
                            .describe('ID of the custom location.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Address of the custom location.')
                            .optional(),
                        })
                        .describe('Information about custom locations.'),
                    }),
                  ])
                )
              )
              .max(500)
              .optional(),
            bookingPolicy: z
              .object({
                _id: z
                  .string()
                  .describe('The ID to the policy for the booking.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                _createdDate: z
                  .date()
                  .describe('Date and time the policy was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the policy was updated.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Name of the policy.')
                  .max(400)
                  .optional()
                  .nullable(),
                customPolicyDescription: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                      )
                      .optional(),
                    description: z
                      .string()
                      .describe(
                        'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                      )
                      .max(2500)
                      .optional(),
                  })
                  .describe(
                    'Custom description for the policy. This policy is displayed to the participant.'
                  )
                  .optional(),
                default: z
                  .boolean()
                  .describe(
                    'Whether the policy is the default for the meta site.'
                  )
                  .optional()
                  .nullable(),
                limitEarlyBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                      )
                      .optional(),
                    earliestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting early bookings.')
                  .optional(),
                limitLateBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting late bookings.')
                  .optional(),
                bookAfterStartPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe(
                    'Policy on booking an entity after the start of the schedule.'
                  )
                  .optional(),
                cancellationPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestCancellation: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestCancellationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for canceling a booked entity.')
                  .optional(),
                reschedulePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestReschedule: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestRescheduleInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for rescheduling a booked entity.')
                  .optional(),
                waitlistPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                      )
                      .optional(),
                    capacity: z
                      .number()
                      .int()
                      .describe(
                        'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                    reservationTimeInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Waitlist policy for the service.')
                  .optional(),
                participantsPolicy: z
                  .object({
                    maxParticipantsPerBooking: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy regarding the participants per booking.')
                  .optional(),
                resourcesPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                      )
                      .optional(),
                    autoAssignAllowed: z
                      .boolean()
                      .describe(
                        '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe('Policy for allocating resources.')
                  .optional(),
                cancellationFeePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                      )
                      .optional(),
                    cancellationWindows: z
                      .array(
                        z.intersection(
                          z.object({
                            startInMinutes: z
                              .number()
                              .int()
                              .describe(
                                'The fee will be applied if the booked session starts within this start time in minutes.'
                              )
                              .min(1)
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({
                              amount: z.never().optional(),
                              percentage: z.never().optional(),
                            }),
                            z.object({
                              percentage: z.never().optional(),
                              amount: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                    )
                                    .optional(),
                                  formattedValue: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                    )
                                    .max(50)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Amount to be charged as a cancellation fee.'
                                ),
                            }),
                            z.object({
                              amount: z.never().optional(),
                              percentage: z
                                .string()
                                .describe(
                                  'Percentage of the original price to be charged as a cancellation fee.'
                                ),
                            }),
                          ])
                        )
                      )
                      .max(2)
                      .optional(),
                    autoCollectFeeEnabled: z
                      .boolean()
                      .describe(
                        'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Rules for cancellation fees.')
                  .optional(),
                saveCreditCardPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe('Default: `false`')
                      .optional(),
                  })
                  .describe('Rule for saving credit card.')
                  .optional(),
              })
              .describe(
                '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
              )
              .optional(),
            schedule: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                firstSessionStart: z
                  .date()
                  .describe(
                    'Start time of the first session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                lastSessionEnd: z
                  .date()
                  .describe(
                    'End time of the last session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                availabilityConstraints: z
                  .object({
                    durations: z
                      .array(
                        z.object({
                          minutes: z
                            .number()
                            .int()
                            .describe(
                              'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                            )
                            .min(1)
                            .max(44639)
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                    sessionDurations: z
                      .array(z.number().int())
                      .max(50)
                      .optional(),
                    timeBetweenSessions: z
                      .number()
                      .int()
                      .describe(
                        'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                      )
                      .min(0)
                      .max(720)
                      .optional(),
                  })
                  .describe('Limitations affecting the service availability.')
                  .optional(),
              })
              .describe(
                "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
              )
              .optional(),
            staffMemberIds: z.array(z.string()).max(220).optional(),
            serviceResources: z
              .array(
                z.object({
                  resourceType: z
                    .object({
                      _id: z
                        .string()
                        .describe('The type of the resource.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('The name of the resource type.')
                        .max(40)
                        .min(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                    )
                    .optional(),
                })
              )
              .max(3)
              .optional(),
            supportedSlugs: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                    )
                    .max(500)
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the slug was created. This is a system field.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(100)
              .optional(),
            mainSlug: z
              .object({
                name: z
                  .string()
                  .describe(
                    "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                  )
                  .max(500)
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                  )
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe(
                    'Date and time the slug was created. This is a system field.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
              )
              .optional(),
            urls: z
              .object({
                servicePage: z
                  .string()
                  .describe('The URL for the service page.')
                  .optional(),
                bookingPage: z
                  .string()
                  .describe(
                    'The URL for the booking entry point. It can be either to the calendar or to the service page.'
                  )
                  .optional(),
                calendarPage: z
                  .string()
                  .describe(
                    'The URL for the calendar. Can be empty if no calendar exists.'
                  )
                  .optional(),
              })
              .describe(
                'URLs to various service-related pages, such as the calendar page and the booking page.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling users to save custom data related to the service.'
              )
              .optional(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Custom SEO data for the service.')
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
              )
              .optional()
              .nullable(),
            addOnGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Name of the add-on group.')
                    .max(100)
                    .optional()
                    .nullable(),
                  maxNumberOfAddOns: z
                    .number()
                    .int()
                    .describe(
                      "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                    )
                    .optional()
                    .nullable(),
                  addOnIds: z.array(z.string()).max(7).optional(),
                  prompt: z
                    .string()
                    .describe(
                      "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(3)
              .optional(),
            addOnDetails: z
              .array(
                z.object({
                  addOnId: z
                    .string()
                    .describe('ID of the add-on.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            taxableAddress: z
              .object({
                taxableAddressType: z
                  .enum(['BUSINESS', 'BILLING'])
                  .describe('Taxable address type.')
                  .optional(),
              })
              .describe('Taxable address used to calculate tax')
              .optional(),
          })
          .describe('Updated service.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Update statistics.')
    .optional(),
});
export const BulkUpdateServicesByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe('Filter to identify the services to update.'),
  options: z.object({
    service: z
      .object({
        _id: z
          .string()
          .describe('Service ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appId: z
          .string()
          .describe(
            'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        type: z.enum(['APPOINTMENT', 'CLASS', 'COURSE']).optional(),
        sortOrder: z
          .number()
          .int()
          .describe(
            'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Service name.')
          .max(400)
          .min(1)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
          )
          .max(7000)
          .optional()
          .nullable(),
        tagLine: z
          .string()
          .describe('Short service description, such as `Hair styling`.')
          .max(6000)
          .optional()
          .nullable(),
        defaultCapacity: z
          .number()
          .int()
          .describe(
            'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        media: z
          .object({
            items: z
              .array(
                z.intersection(
                  z.object({}),
                  z.xor([
                    z.object({ image: z.never().optional() }),
                    z.object({
                      image: z
                        .string()
                        .describe(
                          'Details of the image associated with the service, such as URL and size.'
                        ),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            mainMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Primary media associated with the service.')
              .optional(),
            coverMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Cover media associated with the service.')
              .optional(),
          })
          .describe('Media associated with the service.')
          .optional(),
        hidden: z
          .boolean()
          .describe(
            'Whether the service is hidden from Wix Bookings pages and widgets.'
          )
          .optional()
          .nullable(),
        category: z
          .object({
            _id: z
              .string()
              .describe('Category ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            name: z
              .string()
              .describe('Category name.')
              .max(500)
              .optional()
              .nullable(),
            sortOrder: z
              .number()
              .int()
              .describe('Order of a category within a category list.')
              .optional()
              .nullable(),
          })
          .describe(
            "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
          )
          .optional(),
        form: z
          .object({
            _id: z
              .string()
              .describe(
                'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Form the customer filled out when booking the service.')
          .optional(),
        payment: z
          .intersection(
            z.object({
              rateType: z
                .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                .optional(),
              options: z
                .object({
                  online: z
                    .boolean()
                    .describe(
                      'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                    )
                    .optional()
                    .nullable(),
                  inPerson: z
                    .boolean()
                    .describe('Customers can pay for the service in person.')
                    .optional()
                    .nullable(),
                  deposit: z
                    .boolean()
                    .describe(
                      'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                    )
                    .optional()
                    .nullable(),
                  pricingPlan: z
                    .boolean()
                    .describe(
                      'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The payment options a customer can use to pay for the service.'
                )
                .optional(),
              pricingPlanIds: z.array(z.string()).max(75).optional(),
              addOnOption: z.enum(['ONLINE', 'IN_PERSON']).optional(),
            }),
            z.xor([
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                varied: z.never().optional(),
                fixed: z
                  .object({
                    price: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                varied: z.never().optional(),
                custom: z
                  .object({
                    description: z
                      .string()
                      .describe(
                        'A custom description explaining to the customer how to pay for the service.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z
                  .object({
                    defaultPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    minPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The minimal price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    maxPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The maximum price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                  ),
              }),
            ])
          )
          .describe(
            'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
          )
          .optional(),
        onlineBooking: z
          .object({
            enabled: z
              .boolean()
              .describe(
                'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
              )
              .optional()
              .nullable(),
            requireManualApproval: z
              .boolean()
              .describe(
                'Booking the service requires approval by the Wix user.'
              )
              .optional()
              .nullable(),
            allowMultipleRequests: z
              .boolean()
              .describe(
                'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Online booking settings.')
          .optional(),
        conferencing: z
          .object({
            enabled: z
              .boolean()
              .describe(
                "Whether a conference link is generated for the service's sessions."
              )
              .optional()
              .nullable(),
          })
          .describe('Conferencing options for the service.')
          .optional(),
        locations: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Location ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                type: z.enum(['CUSTOM', 'BUSINESS', 'CUSTOMER']).optional(),
                calculatedAddress: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe(
                    'Location address. Empty for `{"type": "CUSTOMER"}`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  business: z.never().optional(),
                  custom: z.never().optional(),
                }),
                z.object({
                  custom: z.never().optional(),
                  business: z
                    .object({
                      _id: z
                        .string()
                        .describe(
                          'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      name: z
                        .string()
                        .describe('Business location name.')
                        .max(150)
                        .optional(),
                      default: z
                        .boolean()
                        .describe(
                          'Whether this is the default location. There can only be a single default location per site.'
                        )
                        .optional()
                        .nullable(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Business location address.')
                        .optional(),
                      email: z
                        .string()
                        .describe('Business location email.')
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Business location phone.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Information about business locations.'),
                }),
                z.object({
                  business: z.never().optional(),
                  custom: z
                    .object({
                      _id: z
                        .string()
                        .describe('ID of the custom location.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Address of the custom location.')
                        .optional(),
                    })
                    .describe('Information about custom locations.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        bookingPolicy: z
          .object({
            _id: z
              .string()
              .describe('The ID to the policy for the booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the policy was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the policy was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the policy.')
              .max(400)
              .optional()
              .nullable(),
            customPolicyDescription: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                  )
                  .optional(),
                description: z
                  .string()
                  .describe(
                    'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                  )
                  .max(2500)
                  .optional(),
              })
              .describe(
                'Custom description for the policy. This policy is displayed to the participant.'
              )
              .optional(),
            default: z
              .boolean()
              .describe('Whether the policy is the default for the meta site.')
              .optional()
              .nullable(),
            limitEarlyBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                  )
                  .optional(),
                earliestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting early bookings.')
              .optional(),
            limitLateBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                  )
                  .optional(),
                latestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting late bookings.')
              .optional(),
            bookAfterStartPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                'Policy on booking an entity after the start of the schedule.'
              )
              .optional(),
            cancellationPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestCancellation: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestCancellationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for canceling a booked entity.')
              .optional(),
            reschedulePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestReschedule: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestRescheduleInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for rescheduling a booked entity.')
              .optional(),
            waitlistPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                  )
                  .optional(),
                capacity: z
                  .number()
                  .int()
                  .describe(
                    'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
                reservationTimeInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Waitlist policy for the service.')
              .optional(),
            participantsPolicy: z
              .object({
                maxParticipantsPerBooking: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy regarding the participants per booking.')
              .optional(),
            resourcesPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                  )
                  .optional(),
                autoAssignAllowed: z
                  .boolean()
                  .describe(
                    '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe('Policy for allocating resources.')
              .optional(),
            cancellationFeePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                  )
                  .optional(),
                cancellationWindows: z
                  .array(
                    z.intersection(
                      z.object({
                        startInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The fee will be applied if the booked session starts within this start time in minutes.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Amount to be charged as a cancellation fee.'
                            ),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              'Percentage of the original price to be charged as a cancellation fee.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(2)
                  .optional(),
                autoCollectFeeEnabled: z
                  .boolean()
                  .describe(
                    'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Rules for cancellation fees.')
              .optional(),
            saveCreditCardPolicy: z
              .object({
                enabled: z.boolean().describe('Default: `false`').optional(),
              })
              .describe('Rule for saving credit card.')
              .optional(),
          })
          .describe(
            '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
          )
          .optional(),
        schedule: z
          .object({
            _id: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstSessionStart: z
              .date()
              .describe(
                'Start time of the first session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            lastSessionEnd: z
              .date()
              .describe(
                'End time of the last session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            availabilityConstraints: z
              .object({
                durations: z
                  .array(
                    z.object({
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                        )
                        .min(1)
                        .max(44639)
                        .optional(),
                    })
                  )
                  .max(50)
                  .optional(),
                sessionDurations: z.array(z.number().int()).max(50).optional(),
                timeBetweenSessions: z
                  .number()
                  .int()
                  .describe(
                    'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                  )
                  .min(0)
                  .max(720)
                  .optional(),
              })
              .describe('Limitations affecting the service availability.')
              .optional(),
          })
          .describe(
            "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
          )
          .optional(),
        staffMemberIds: z.array(z.string()).max(220).optional(),
        serviceResources: z
          .array(
            z.object({
              resourceType: z
                .object({
                  _id: z
                    .string()
                    .describe('The type of the resource.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('The name of the resource type.')
                    .max(40)
                    .min(1)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                )
                .optional(),
            })
          )
          .max(3)
          .optional(),
        supportedSlugs: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                )
                .max(500)
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the slug was created. This is a system field.'
                )
                .optional()
                .nullable(),
            })
          )
          .max(100)
          .optional(),
        mainSlug: z
          .object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
          )
          .optional(),
        urls: z
          .object({
            servicePage: z
              .string()
              .describe('The URL for the service page.')
              .optional(),
            bookingPage: z
              .string()
              .describe(
                'The URL for the booking entry point. It can be either to the calendar or to the service page.'
              )
              .optional(),
            calendarPage: z
              .string()
              .describe(
                'The URL for the calendar. Can be empty if no calendar exists.'
              )
              .optional(),
          })
          .describe(
            'URLs to various service-related pages, such as the calendar page and the booking page.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling users to save custom data related to the service.'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Custom SEO data for the service.')
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
          )
          .optional()
          .nullable(),
        addOnGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe(
                  'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Name of the add-on group.')
                .max(100)
                .optional()
                .nullable(),
              maxNumberOfAddOns: z
                .number()
                .int()
                .describe(
                  "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                )
                .optional()
                .nullable(),
              addOnIds: z.array(z.string()).max(7).optional(),
              prompt: z
                .string()
                .describe(
                  "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                )
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(3)
          .optional(),
        addOnDetails: z
          .array(
            z.object({
              addOnId: z
                .string()
                .describe('ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                )
                .optional()
                .nullable(),
            })
          )
          .max(50)
          .optional(),
        taxableAddress: z
          .object({
            taxableAddressType: z.enum(['BUSINESS', 'BILLING']).optional(),
          })
          .describe('Taxable address used to calculate tax')
          .optional(),
      })
      .describe('Service to update.'),
  }),
});
export const BulkUpdateServicesByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'ID of the service update job.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/introduction) to retrieve job details and metadata.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const DeleteServiceRequest = z.object({
  serviceId: z
    .string()
    .describe('ID of the service to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      preserveFutureSessionsWithParticipants: z
        .boolean()
        .describe(
          'Whether to preserve future sessions with participants.\n\nDefault: `false`'
        )
        .optional(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Whether to notify participants about the change and an optional\ncustom message.'
        )
        .optional(),
    })
    .describe(
      "Allows you to configure how to handle the deleted service's future sessions and how to notify the sessions participants."
    )
    .optional(),
});
export const DeleteServiceResponse = z.object({});
export const BulkDeleteServicesRequest = z.object({
  ids: z.array(z.string()).max(100),
  options: z
    .object({
      preserveFutureSessionsWithParticipants: z
        .boolean()
        .describe(
          'Whether to preserve future sessions with participants.\n\nDefault: `false`.'
        )
        .optional(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Whether to notify participants about the change and an optional\ncustom message.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkDeleteServicesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Update metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Service ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appId: z
              .string()
              .describe(
                'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            createdByAppId: z
              .string()
              .describe(
                "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
              .describe(
                'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
              )
              .optional(),
            sortOrder: z
              .number()
              .int()
              .describe(
                'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Service name.')
              .max(400)
              .min(1)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe(
                'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
              )
              .max(7000)
              .optional()
              .nullable(),
            tagLine: z
              .string()
              .describe('Short service description, such as `Hair styling`.')
              .max(6000)
              .optional()
              .nullable(),
            defaultCapacity: z
              .number()
              .int()
              .describe(
                'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
              )
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
            media: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({ image: z.never().optional() }),
                        z.object({
                          image: z
                            .string()
                            .describe(
                              'Details of the image associated with the service, such as URL and size.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(100)
                  .optional(),
                mainMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Primary media associated with the service.')
                  .optional(),
                coverMedia: z
                  .intersection(
                    z.object({}),
                    z.xor([
                      z.object({ image: z.never().optional() }),
                      z.object({
                        image: z
                          .string()
                          .describe(
                            'Details of the image associated with the service, such as URL and size.'
                          ),
                      }),
                    ])
                  )
                  .describe('Cover media associated with the service.')
                  .optional(),
              })
              .describe('Media associated with the service.')
              .optional(),
            hidden: z
              .boolean()
              .describe(
                'Whether the service is hidden from Wix Bookings pages and widgets.'
              )
              .optional()
              .nullable(),
            category: z
              .object({
                _id: z
                  .string()
                  .describe('Category ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Category name.')
                  .max(500)
                  .optional()
                  .nullable(),
                sortOrder: z
                  .number()
                  .int()
                  .describe('Order of a category within a category list.')
                  .optional()
                  .nullable(),
              })
              .describe(
                "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
              )
              .optional(),
            form: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Form the customer filled out when booking the service.'
              )
              .optional(),
            payment: z
              .intersection(
                z.object({
                  rateType: z
                    .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                    .describe(
                      'The rate the customer is expected to pay for the service.'
                    )
                    .optional(),
                  options: z
                    .object({
                      online: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                        )
                        .optional()
                        .nullable(),
                      inPerson: z
                        .boolean()
                        .describe(
                          'Customers can pay for the service in person.'
                        )
                        .optional()
                        .nullable(),
                      deposit: z
                        .boolean()
                        .describe(
                          'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                        )
                        .optional()
                        .nullable(),
                      pricingPlan: z
                        .boolean()
                        .describe(
                          'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The payment options a customer can use to pay for the service.'
                    )
                    .optional(),
                  pricingPlanIds: z.array(z.string()).max(75).optional(),
                  addOnOption: z
                    .enum(['ONLINE', 'IN_PERSON'])
                    .describe(
                      'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    varied: z.never().optional(),
                    fixed: z
                      .object({
                        price: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    varied: z.never().optional(),
                    custom: z
                      .object({
                        description: z
                          .string()
                          .describe(
                            'A custom description explaining to the customer how to pay for the service.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                      ),
                  }),
                  z.object({
                    fixed: z.never().optional(),
                    custom: z.never().optional(),
                    varied: z
                      .object({
                        defaultPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                          )
                          .optional(),
                        deposit: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                          )
                          .optional(),
                        minPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The minimal price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        maxPrice: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'The maximum price a customer may pay for this service, based on its variants.'
                          )
                          .optional(),
                        fullUpfrontPaymentAllowed: z
                          .boolean()
                          .describe(
                            'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                      ),
                  }),
                ])
              )
              .describe(
                'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
              )
              .optional(),
            onlineBooking: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
                  )
                  .optional()
                  .nullable(),
                requireManualApproval: z
                  .boolean()
                  .describe(
                    'Booking the service requires approval by the Wix user.'
                  )
                  .optional()
                  .nullable(),
                allowMultipleRequests: z
                  .boolean()
                  .describe(
                    'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Online booking settings.')
              .optional(),
            conferencing: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    "Whether a conference link is generated for the service's sessions."
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Conferencing options for the service.')
              .optional(),
            locations: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Location ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    type: z
                      .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                      .describe('Location type.\n\nDefault: `CUSTOM`')
                      .optional(),
                    calculatedAddress: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe(
                        'Location address. Empty for `{"type": "CUSTOMER"}`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      business: z.never().optional(),
                      custom: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      business: z
                        .object({
                          _id: z
                            .string()
                            .describe(
                              'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .string()
                            .describe('Business location name.')
                            .max(150)
                            .optional(),
                          default: z
                            .boolean()
                            .describe(
                              'Whether this is the default location. There can only be a single default location per site.'
                            )
                            .optional()
                            .nullable(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Business location address.')
                            .optional(),
                          email: z
                            .string()
                            .describe('Business location email.')
                            .email()
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Business location phone.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Information about business locations.'),
                    }),
                    z.object({
                      business: z.never().optional(),
                      custom: z
                        .object({
                          _id: z
                            .string()
                            .describe('ID of the custom location.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          address: z
                            .object({
                              city: z.string().optional().nullable(),
                              subdivision: z.string().optional().nullable(),
                              country: z.string().optional().nullable(),
                              postalCode: z.string().optional().nullable(),
                              addressLine1: z.string().optional().nullable(),
                            })
                            .describe('Address of the custom location.')
                            .optional(),
                        })
                        .describe('Information about custom locations.'),
                    }),
                  ])
                )
              )
              .max(500)
              .optional(),
            bookingPolicy: z
              .object({
                _id: z
                  .string()
                  .describe('The ID to the policy for the booking.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                _createdDate: z
                  .date()
                  .describe('Date and time the policy was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the policy was updated.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Name of the policy.')
                  .max(400)
                  .optional()
                  .nullable(),
                customPolicyDescription: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                      )
                      .optional(),
                    description: z
                      .string()
                      .describe(
                        'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                      )
                      .max(2500)
                      .optional(),
                  })
                  .describe(
                    'Custom description for the policy. This policy is displayed to the participant.'
                  )
                  .optional(),
                default: z
                  .boolean()
                  .describe(
                    'Whether the policy is the default for the meta site.'
                  )
                  .optional()
                  .nullable(),
                limitEarlyBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                      )
                      .optional(),
                    earliestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting early bookings.')
                  .optional(),
                limitLateBookingPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestBookingInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy for limiting late bookings.')
                  .optional(),
                bookAfterStartPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe(
                    'Policy on booking an entity after the start of the schedule.'
                  )
                  .optional(),
                cancellationPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestCancellation: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestCancellationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for canceling a booked entity.')
                  .optional(),
                reschedulePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                      )
                      .optional(),
                    limitLatestReschedule: z
                      .boolean()
                      .describe(
                        'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                      )
                      .optional(),
                    latestRescheduleInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                      )
                      .min(1)
                      .optional(),
                    allowAnonymous: z
                      .boolean()
                      .describe(
                        "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Policy for rescheduling a booked entity.')
                  .optional(),
                waitlistPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                      )
                      .optional(),
                    capacity: z
                      .number()
                      .int()
                      .describe(
                        'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                    reservationTimeInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Waitlist policy for the service.')
                  .optional(),
                participantsPolicy: z
                  .object({
                    maxParticipantsPerBooking: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                      )
                      .min(1)
                      .optional(),
                  })
                  .describe('Policy regarding the participants per booking.')
                  .optional(),
                resourcesPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                      )
                      .optional(),
                    autoAssignAllowed: z
                      .boolean()
                      .describe(
                        '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                      )
                      .optional(),
                  })
                  .describe('Policy for allocating resources.')
                  .optional(),
                cancellationFeePolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                      )
                      .optional(),
                    cancellationWindows: z
                      .array(
                        z.intersection(
                          z.object({
                            startInMinutes: z
                              .number()
                              .int()
                              .describe(
                                'The fee will be applied if the booked session starts within this start time in minutes.'
                              )
                              .min(1)
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({
                              amount: z.never().optional(),
                              percentage: z.never().optional(),
                            }),
                            z.object({
                              percentage: z.never().optional(),
                              amount: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                    )
                                    .optional(),
                                  formattedValue: z
                                    .string()
                                    .describe(
                                      'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                    )
                                    .max(50)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Amount to be charged as a cancellation fee.'
                                ),
                            }),
                            z.object({
                              amount: z.never().optional(),
                              percentage: z
                                .string()
                                .describe(
                                  'Percentage of the original price to be charged as a cancellation fee.'
                                ),
                            }),
                          ])
                        )
                      )
                      .max(2)
                      .optional(),
                    autoCollectFeeEnabled: z
                      .boolean()
                      .describe(
                        'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Rules for cancellation fees.')
                  .optional(),
                saveCreditCardPolicy: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe('Default: `false`')
                      .optional(),
                  })
                  .describe('Rule for saving credit card.')
                  .optional(),
              })
              .describe(
                '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
              )
              .optional(),
            schedule: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                firstSessionStart: z
                  .date()
                  .describe(
                    'Start time of the first session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                lastSessionEnd: z
                  .date()
                  .describe(
                    'End time of the last session in the schedule. For courses only.'
                  )
                  .optional()
                  .nullable(),
                availabilityConstraints: z
                  .object({
                    durations: z
                      .array(
                        z.object({
                          minutes: z
                            .number()
                            .int()
                            .describe(
                              'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                            )
                            .min(1)
                            .max(44639)
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                    sessionDurations: z
                      .array(z.number().int())
                      .max(50)
                      .optional(),
                    timeBetweenSessions: z
                      .number()
                      .int()
                      .describe(
                        'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                      )
                      .min(0)
                      .max(720)
                      .optional(),
                  })
                  .describe('Limitations affecting the service availability.')
                  .optional(),
              })
              .describe(
                "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
              )
              .optional(),
            staffMemberIds: z.array(z.string()).max(220).optional(),
            serviceResources: z
              .array(
                z.object({
                  resourceType: z
                    .object({
                      _id: z
                        .string()
                        .describe('The type of the resource.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('The name of the resource type.')
                        .max(40)
                        .min(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                    )
                    .optional(),
                })
              )
              .max(3)
              .optional(),
            supportedSlugs: z
              .array(
                z.object({
                  name: z
                    .string()
                    .describe(
                      "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                    )
                    .max(500)
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the slug was created. This is a system field.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(100)
              .optional(),
            mainSlug: z
              .object({
                name: z
                  .string()
                  .describe(
                    "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                  )
                  .max(500)
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                  )
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe(
                    'Date and time the slug was created. This is a system field.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
              )
              .optional(),
            urls: z
              .object({
                servicePage: z
                  .string()
                  .describe('The URL for the service page.')
                  .optional(),
                bookingPage: z
                  .string()
                  .describe(
                    'The URL for the booking entry point. It can be either to the calendar or to the service page.'
                  )
                  .optional(),
                calendarPage: z
                  .string()
                  .describe(
                    'The URL for the calendar. Can be empty if no calendar exists.'
                  )
                  .optional(),
              })
              .describe(
                'URLs to various service-related pages, such as the calendar page and the booking page.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Extensions enabling users to save custom data related to the service.'
              )
              .optional(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Custom SEO data for the service.')
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
              )
              .optional()
              .nullable(),
            addOnGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Name of the add-on group.')
                    .max(100)
                    .optional()
                    .nullable(),
                  maxNumberOfAddOns: z
                    .number()
                    .int()
                    .describe(
                      "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                    )
                    .optional()
                    .nullable(),
                  addOnIds: z.array(z.string()).max(7).optional(),
                  prompt: z
                    .string()
                    .describe(
                      "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(3)
              .optional(),
            addOnDetails: z
              .array(
                z.object({
                  addOnId: z
                    .string()
                    .describe('ID of the add-on.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(50)
              .optional(),
            taxableAddress: z
              .object({
                taxableAddressType: z
                  .enum(['BUSINESS', 'BILLING'])
                  .describe('Taxable address type.')
                  .optional(),
              })
              .describe('Taxable address used to calculate tax')
              .optional(),
          })
          .describe('Updated service.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Delete statistics.')
    .optional(),
});
export const BulkDeleteServicesByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe('Filter to identify the services that need to be deleted.'),
  options: z
    .object({
      preserveFutureSessionsWithParticipants: z
        .boolean()
        .describe(
          'Whether to preserve future sessions with participants.\n\nDefault: `false`.'
        )
        .optional(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Whether to notify participants about the change and an optional custom message.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkDeleteServicesByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'ID of the service deletion job.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/introduction) to retrieve job details and metadata.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const QueryServicesRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          appId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          type: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          hidden: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          tagLine: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          staffMemberIds: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'form._id': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'category._id': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'category.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'locations.business._id': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'mainSlug.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'supportedSlugs.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'onlineBooking.enabled': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'payment.options.online': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'payment.options.inPerson': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'payment.options.pricingPlan': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'schedule.firstSessionStart': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'schedule.lastSessionEnd': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'appId',
                'name',
                'type',
                'description',
                'hidden',
                'tagLine',
                'staffMemberIds',
                'form._id',
                'category._id',
                'category.name',
                'locations.business._id',
                'mainSlug.name',
                'supportedSlugs.name',
                'onlineBooking.enabled',
                'payment.options.online',
                'payment.options.inPerson',
                'payment.options.pricingPlan',
                'schedule.firstSessionStart',
                'schedule.lastSessionEnd',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('WQL expression.'),
});
export const QueryServicesResponse = z.object({
  services: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Service ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appId: z
          .string()
          .describe(
            'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        type: z
          .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
          .describe(
            'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
          )
          .optional(),
        sortOrder: z
          .number()
          .int()
          .describe(
            'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Service name.')
          .max(400)
          .min(1)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
          )
          .max(7000)
          .optional()
          .nullable(),
        tagLine: z
          .string()
          .describe('Short service description, such as `Hair styling`.')
          .max(6000)
          .optional()
          .nullable(),
        defaultCapacity: z
          .number()
          .int()
          .describe(
            'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        media: z
          .object({
            items: z
              .array(
                z.intersection(
                  z.object({}),
                  z.xor([
                    z.object({ image: z.never().optional() }),
                    z.object({
                      image: z
                        .string()
                        .describe(
                          'Details of the image associated with the service, such as URL and size.'
                        ),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            mainMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Primary media associated with the service.')
              .optional(),
            coverMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Cover media associated with the service.')
              .optional(),
          })
          .describe('Media associated with the service.')
          .optional(),
        hidden: z
          .boolean()
          .describe(
            'Whether the service is hidden from Wix Bookings pages and widgets.'
          )
          .optional()
          .nullable(),
        category: z
          .object({
            _id: z
              .string()
              .describe('Category ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            name: z
              .string()
              .describe('Category name.')
              .max(500)
              .optional()
              .nullable(),
            sortOrder: z
              .number()
              .int()
              .describe('Order of a category within a category list.')
              .optional()
              .nullable(),
          })
          .describe(
            "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
          )
          .optional(),
        form: z
          .object({
            _id: z
              .string()
              .describe(
                'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Form the customer filled out when booking the service.')
          .optional(),
        payment: z
          .intersection(
            z.object({
              rateType: z
                .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                .describe(
                  'The rate the customer is expected to pay for the service.'
                )
                .optional(),
              options: z
                .object({
                  online: z
                    .boolean()
                    .describe(
                      'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                    )
                    .optional()
                    .nullable(),
                  inPerson: z
                    .boolean()
                    .describe('Customers can pay for the service in person.')
                    .optional()
                    .nullable(),
                  deposit: z
                    .boolean()
                    .describe(
                      'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                    )
                    .optional()
                    .nullable(),
                  pricingPlan: z
                    .boolean()
                    .describe(
                      'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The payment options a customer can use to pay for the service.'
                )
                .optional(),
              pricingPlanIds: z.array(z.string()).max(75).optional(),
              addOnOption: z
                .enum(['ONLINE', 'IN_PERSON'])
                .describe(
                  'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                varied: z.never().optional(),
                fixed: z
                  .object({
                    price: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                varied: z.never().optional(),
                custom: z
                  .object({
                    description: z
                      .string()
                      .describe(
                        'A custom description explaining to the customer how to pay for the service.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z
                  .object({
                    defaultPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    minPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The minimal price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    maxPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The maximum price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                  ),
              }),
            ])
          )
          .describe(
            'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
          )
          .optional(),
        onlineBooking: z
          .object({
            enabled: z
              .boolean()
              .describe(
                'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
              )
              .optional()
              .nullable(),
            requireManualApproval: z
              .boolean()
              .describe(
                'Booking the service requires approval by the Wix user.'
              )
              .optional()
              .nullable(),
            allowMultipleRequests: z
              .boolean()
              .describe(
                'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Online booking settings.')
          .optional(),
        conferencing: z
          .object({
            enabled: z
              .boolean()
              .describe(
                "Whether a conference link is generated for the service's sessions."
              )
              .optional()
              .nullable(),
          })
          .describe('Conferencing options for the service.')
          .optional(),
        locations: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Location ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                type: z
                  .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                  .describe('Location type.\n\nDefault: `CUSTOM`')
                  .optional(),
                calculatedAddress: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe(
                    'Location address. Empty for `{"type": "CUSTOMER"}`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  business: z.never().optional(),
                  custom: z.never().optional(),
                }),
                z.object({
                  custom: z.never().optional(),
                  business: z
                    .object({
                      _id: z
                        .string()
                        .describe(
                          'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      name: z
                        .string()
                        .describe('Business location name.')
                        .max(150)
                        .optional(),
                      default: z
                        .boolean()
                        .describe(
                          'Whether this is the default location. There can only be a single default location per site.'
                        )
                        .optional()
                        .nullable(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Business location address.')
                        .optional(),
                      email: z
                        .string()
                        .describe('Business location email.')
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Business location phone.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Information about business locations.'),
                }),
                z.object({
                  business: z.never().optional(),
                  custom: z
                    .object({
                      _id: z
                        .string()
                        .describe('ID of the custom location.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Address of the custom location.')
                        .optional(),
                    })
                    .describe('Information about custom locations.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        bookingPolicy: z
          .object({
            _id: z
              .string()
              .describe('The ID to the policy for the booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the policy was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the policy was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the policy.')
              .max(400)
              .optional()
              .nullable(),
            customPolicyDescription: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                  )
                  .optional(),
                description: z
                  .string()
                  .describe(
                    'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                  )
                  .max(2500)
                  .optional(),
              })
              .describe(
                'Custom description for the policy. This policy is displayed to the participant.'
              )
              .optional(),
            default: z
              .boolean()
              .describe('Whether the policy is the default for the meta site.')
              .optional()
              .nullable(),
            limitEarlyBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                  )
                  .optional(),
                earliestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting early bookings.')
              .optional(),
            limitLateBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                  )
                  .optional(),
                latestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting late bookings.')
              .optional(),
            bookAfterStartPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                'Policy on booking an entity after the start of the schedule.'
              )
              .optional(),
            cancellationPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestCancellation: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestCancellationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for canceling a booked entity.')
              .optional(),
            reschedulePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestReschedule: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestRescheduleInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for rescheduling a booked entity.')
              .optional(),
            waitlistPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                  )
                  .optional(),
                capacity: z
                  .number()
                  .int()
                  .describe(
                    'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
                reservationTimeInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Waitlist policy for the service.')
              .optional(),
            participantsPolicy: z
              .object({
                maxParticipantsPerBooking: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy regarding the participants per booking.')
              .optional(),
            resourcesPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                  )
                  .optional(),
                autoAssignAllowed: z
                  .boolean()
                  .describe(
                    '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe('Policy for allocating resources.')
              .optional(),
            cancellationFeePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                  )
                  .optional(),
                cancellationWindows: z
                  .array(
                    z.intersection(
                      z.object({
                        startInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The fee will be applied if the booked session starts within this start time in minutes.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Amount to be charged as a cancellation fee.'
                            ),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              'Percentage of the original price to be charged as a cancellation fee.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(2)
                  .optional(),
                autoCollectFeeEnabled: z
                  .boolean()
                  .describe(
                    'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Rules for cancellation fees.')
              .optional(),
            saveCreditCardPolicy: z
              .object({
                enabled: z.boolean().describe('Default: `false`').optional(),
              })
              .describe('Rule for saving credit card.')
              .optional(),
          })
          .describe(
            '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
          )
          .optional(),
        schedule: z
          .object({
            _id: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstSessionStart: z
              .date()
              .describe(
                'Start time of the first session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            lastSessionEnd: z
              .date()
              .describe(
                'End time of the last session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            availabilityConstraints: z
              .object({
                durations: z
                  .array(
                    z.object({
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                        )
                        .min(1)
                        .max(44639)
                        .optional(),
                    })
                  )
                  .max(50)
                  .optional(),
                sessionDurations: z.array(z.number().int()).max(50).optional(),
                timeBetweenSessions: z
                  .number()
                  .int()
                  .describe(
                    'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                  )
                  .min(0)
                  .max(720)
                  .optional(),
              })
              .describe('Limitations affecting the service availability.')
              .optional(),
          })
          .describe(
            "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
          )
          .optional(),
        staffMemberIds: z.array(z.string()).max(220).optional(),
        serviceResources: z
          .array(
            z.object({
              resourceType: z
                .object({
                  _id: z
                    .string()
                    .describe('The type of the resource.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('The name of the resource type.')
                    .max(40)
                    .min(1)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                )
                .optional(),
            })
          )
          .max(3)
          .optional(),
        supportedSlugs: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                )
                .max(500)
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the slug was created. This is a system field.'
                )
                .optional()
                .nullable(),
            })
          )
          .max(100)
          .optional(),
        mainSlug: z
          .object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
          )
          .optional(),
        urls: z
          .object({
            servicePage: z
              .string()
              .describe('The URL for the service page.')
              .optional(),
            bookingPage: z
              .string()
              .describe(
                'The URL for the booking entry point. It can be either to the calendar or to the service page.'
              )
              .optional(),
            calendarPage: z
              .string()
              .describe(
                'The URL for the calendar. Can be empty if no calendar exists.'
              )
              .optional(),
          })
          .describe(
            'URLs to various service-related pages, such as the calendar page and the booking page.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling users to save custom data related to the service.'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Custom SEO data for the service.')
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
          )
          .optional()
          .nullable(),
        addOnGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe(
                  'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Name of the add-on group.')
                .max(100)
                .optional()
                .nullable(),
              maxNumberOfAddOns: z
                .number()
                .int()
                .describe(
                  "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                )
                .optional()
                .nullable(),
              addOnIds: z.array(z.string()).max(7).optional(),
              prompt: z
                .string()
                .describe(
                  "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                )
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(3)
          .optional(),
        addOnDetails: z
          .array(
            z.object({
              addOnId: z
                .string()
                .describe('ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                )
                .optional()
                .nullable(),
            })
          )
          .max(50)
          .optional(),
        taxableAddress: z
          .object({
            taxableAddressType: z
              .enum(['BUSINESS', 'BILLING'])
              .describe('Taxable address type.')
              .optional(),
          })
          .describe('Taxable address used to calculate tax')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor token for retrieving the next page of results.\n\nUse this token in subsequent requests to continue pagination forward.\nValue is `null` when on the last page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor token for retrieving the previous page of results.\n\nUse this token to navigate backwards through result pages.\nValue is `null` when on the first page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata, including offset and count.')
    .optional(),
});
export const SearchServicesRequest = z.object({
  search: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          appId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          type: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          hidden: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          tagLine: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          staffMemberIds: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'form._id': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'category._id': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'category.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'category.sortOrder': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'locations.business._id': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'locations.business.name': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'locations.type': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'locations.calculatedAddress.formattedAddress': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'mainSlug.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'supportedSlugs.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'onlineBooking.enabled': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'payment.options.online': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'payment.options.inPerson': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'payment.options.pricingPlan': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'addOnDetails.addOnId': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'addOnDetails.durationInMinutes': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'appId',
                'name',
                'type',
                'description',
                'hidden',
                'tagLine',
                'staffMemberIds',
                'form._id',
                'category._id',
                'category.name',
                'category.sortOrder',
                'locations.business._id',
                'locations.business.name',
                'locations.type',
                'locations.calculatedAddress.formattedAddress',
                'mainSlug.name',
                'supportedSlugs.name',
                'onlineBooking.enabled',
                'payment.options.online',
                'payment.options.inPerson',
                'payment.options.pricingPlan',
                'addOnDetails.addOnId',
                'addOnDetails.durationInMinutes',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Search criteria including filter, sort, aggregations, and paging options.'
    ),
});
export const SearchServicesResponse = z.object({
  services: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Service ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appId: z
          .string()
          .describe(
            'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        type: z
          .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
          .describe(
            'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
          )
          .optional(),
        sortOrder: z
          .number()
          .int()
          .describe(
            'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Service name.')
          .max(400)
          .min(1)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
          )
          .max(7000)
          .optional()
          .nullable(),
        tagLine: z
          .string()
          .describe('Short service description, such as `Hair styling`.')
          .max(6000)
          .optional()
          .nullable(),
        defaultCapacity: z
          .number()
          .int()
          .describe(
            'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
          )
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
        media: z
          .object({
            items: z
              .array(
                z.intersection(
                  z.object({}),
                  z.xor([
                    z.object({ image: z.never().optional() }),
                    z.object({
                      image: z
                        .string()
                        .describe(
                          'Details of the image associated with the service, such as URL and size.'
                        ),
                    }),
                  ])
                )
              )
              .max(100)
              .optional(),
            mainMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Primary media associated with the service.')
              .optional(),
            coverMedia: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
              .describe('Cover media associated with the service.')
              .optional(),
          })
          .describe('Media associated with the service.')
          .optional(),
        hidden: z
          .boolean()
          .describe(
            'Whether the service is hidden from Wix Bookings pages and widgets.'
          )
          .optional()
          .nullable(),
        category: z
          .object({
            _id: z
              .string()
              .describe('Category ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            name: z
              .string()
              .describe('Category name.')
              .max(500)
              .optional()
              .nullable(),
            sortOrder: z
              .number()
              .int()
              .describe('Order of a category within a category list.')
              .optional()
              .nullable(),
          })
          .describe(
            "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
          )
          .optional(),
        form: z
          .object({
            _id: z
              .string()
              .describe(
                'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Form the customer filled out when booking the service.')
          .optional(),
        payment: z
          .intersection(
            z.object({
              rateType: z
                .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                .describe(
                  'The rate the customer is expected to pay for the service.'
                )
                .optional(),
              options: z
                .object({
                  online: z
                    .boolean()
                    .describe(
                      'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                    )
                    .optional()
                    .nullable(),
                  inPerson: z
                    .boolean()
                    .describe('Customers can pay for the service in person.')
                    .optional()
                    .nullable(),
                  deposit: z
                    .boolean()
                    .describe(
                      'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                    )
                    .optional()
                    .nullable(),
                  pricingPlan: z
                    .boolean()
                    .describe(
                      'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The payment options a customer can use to pay for the service.'
                )
                .optional(),
              pricingPlanIds: z.array(z.string()).max(75).optional(),
              addOnOption: z
                .enum(['ONLINE', 'IN_PERSON'])
                .describe(
                  'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                varied: z.never().optional(),
                fixed: z
                  .object({
                    price: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                varied: z.never().optional(),
                custom: z
                  .object({
                    description: z
                      .string()
                      .describe(
                        'A custom description explaining to the customer how to pay for the service.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                  ),
              }),
              z.object({
                fixed: z.never().optional(),
                custom: z.never().optional(),
                varied: z
                  .object({
                    defaultPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                      )
                      .optional(),
                    deposit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                      )
                      .optional(),
                    minPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The minimal price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    maxPrice: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                          )
                          .optional(),
                        formattedValue: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                          )
                          .max(50)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The maximum price a customer may pay for this service, based on its variants.'
                      )
                      .optional(),
                    fullUpfrontPaymentAllowed: z
                      .boolean()
                      .describe(
                        'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                  ),
              }),
            ])
          )
          .describe(
            'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
          )
          .optional(),
        onlineBooking: z
          .object({
            enabled: z
              .boolean()
              .describe(
                'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
              )
              .optional()
              .nullable(),
            requireManualApproval: z
              .boolean()
              .describe(
                'Booking the service requires approval by the Wix user.'
              )
              .optional()
              .nullable(),
            allowMultipleRequests: z
              .boolean()
              .describe(
                'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Online booking settings.')
          .optional(),
        conferencing: z
          .object({
            enabled: z
              .boolean()
              .describe(
                "Whether a conference link is generated for the service's sessions."
              )
              .optional()
              .nullable(),
          })
          .describe('Conferencing options for the service.')
          .optional(),
        locations: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Location ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                type: z
                  .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                  .describe('Location type.\n\nDefault: `CUSTOM`')
                  .optional(),
                calculatedAddress: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                  })
                  .describe(
                    'Location address. Empty for `{"type": "CUSTOMER"}`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  business: z.never().optional(),
                  custom: z.never().optional(),
                }),
                z.object({
                  custom: z.never().optional(),
                  business: z
                    .object({
                      _id: z
                        .string()
                        .describe(
                          'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      name: z
                        .string()
                        .describe('Business location name.')
                        .max(150)
                        .optional(),
                      default: z
                        .boolean()
                        .describe(
                          'Whether this is the default location. There can only be a single default location per site.'
                        )
                        .optional()
                        .nullable(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Business location address.')
                        .optional(),
                      email: z
                        .string()
                        .describe('Business location email.')
                        .email()
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Business location phone.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Information about business locations.'),
                }),
                z.object({
                  business: z.never().optional(),
                  custom: z
                    .object({
                      _id: z
                        .string()
                        .describe('ID of the custom location.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe('Address of the custom location.')
                        .optional(),
                    })
                    .describe('Information about custom locations.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        bookingPolicy: z
          .object({
            _id: z
              .string()
              .describe('The ID to the policy for the booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the policy was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the policy was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the policy.')
              .max(400)
              .optional()
              .nullable(),
            customPolicyDescription: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                  )
                  .optional(),
                description: z
                  .string()
                  .describe(
                    'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                  )
                  .max(2500)
                  .optional(),
              })
              .describe(
                'Custom description for the policy. This policy is displayed to the participant.'
              )
              .optional(),
            default: z
              .boolean()
              .describe('Whether the policy is the default for the meta site.')
              .optional()
              .nullable(),
            limitEarlyBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                  )
                  .optional(),
                earliestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting early bookings.')
              .optional(),
            limitLateBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                  )
                  .optional(),
                latestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting late bookings.')
              .optional(),
            bookAfterStartPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                'Policy on booking an entity after the start of the schedule.'
              )
              .optional(),
            cancellationPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestCancellation: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestCancellationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for canceling a booked entity.')
              .optional(),
            reschedulePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestReschedule: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestRescheduleInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for rescheduling a booked entity.')
              .optional(),
            waitlistPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                  )
                  .optional(),
                capacity: z
                  .number()
                  .int()
                  .describe(
                    'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
                reservationTimeInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Waitlist policy for the service.')
              .optional(),
            participantsPolicy: z
              .object({
                maxParticipantsPerBooking: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy regarding the participants per booking.')
              .optional(),
            resourcesPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                  )
                  .optional(),
                autoAssignAllowed: z
                  .boolean()
                  .describe(
                    '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe('Policy for allocating resources.')
              .optional(),
            cancellationFeePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                  )
                  .optional(),
                cancellationWindows: z
                  .array(
                    z.intersection(
                      z.object({
                        startInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The fee will be applied if the booked session starts within this start time in minutes.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Amount to be charged as a cancellation fee.'
                            ),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              'Percentage of the original price to be charged as a cancellation fee.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(2)
                  .optional(),
                autoCollectFeeEnabled: z
                  .boolean()
                  .describe(
                    'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Rules for cancellation fees.')
              .optional(),
            saveCreditCardPolicy: z
              .object({
                enabled: z.boolean().describe('Default: `false`').optional(),
              })
              .describe('Rule for saving credit card.')
              .optional(),
          })
          .describe(
            '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
          )
          .optional(),
        schedule: z
          .object({
            _id: z
              .string()
              .describe(
                "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstSessionStart: z
              .date()
              .describe(
                'Start time of the first session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            lastSessionEnd: z
              .date()
              .describe(
                'End time of the last session in the schedule. For courses only.'
              )
              .optional()
              .nullable(),
            availabilityConstraints: z
              .object({
                durations: z
                  .array(
                    z.object({
                      minutes: z
                        .number()
                        .int()
                        .describe(
                          'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                        )
                        .min(1)
                        .max(44639)
                        .optional(),
                    })
                  )
                  .max(50)
                  .optional(),
                sessionDurations: z.array(z.number().int()).max(50).optional(),
                timeBetweenSessions: z
                  .number()
                  .int()
                  .describe(
                    'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                  )
                  .min(0)
                  .max(720)
                  .optional(),
              })
              .describe('Limitations affecting the service availability.')
              .optional(),
          })
          .describe(
            "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
          )
          .optional(),
        staffMemberIds: z.array(z.string()).max(220).optional(),
        serviceResources: z
          .array(
            z.object({
              resourceType: z
                .object({
                  _id: z
                    .string()
                    .describe('The type of the resource.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('The name of the resource type.')
                    .max(40)
                    .min(1)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                )
                .optional(),
            })
          )
          .max(3)
          .optional(),
        supportedSlugs: z
          .array(
            z.object({
              name: z
                .string()
                .describe(
                  "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                )
                .max(500)
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the slug was created. This is a system field.'
                )
                .optional()
                .nullable(),
            })
          )
          .max(100)
          .optional(),
        mainSlug: z
          .object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
          )
          .optional(),
        urls: z
          .object({
            servicePage: z
              .string()
              .describe('The URL for the service page.')
              .optional(),
            bookingPage: z
              .string()
              .describe(
                'The URL for the booking entry point. It can be either to the calendar or to the service page.'
              )
              .optional(),
            calendarPage: z
              .string()
              .describe(
                'The URL for the calendar. Can be empty if no calendar exists.'
              )
              .optional(),
          })
          .describe(
            'URLs to various service-related pages, such as the calendar page and the booking page.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling users to save custom data related to the service.'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Custom SEO data for the service.')
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
          )
          .optional()
          .nullable(),
        addOnGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe(
                  'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Name of the add-on group.')
                .max(100)
                .optional()
                .nullable(),
              maxNumberOfAddOns: z
                .number()
                .int()
                .describe(
                  "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                )
                .optional()
                .nullable(),
              addOnIds: z.array(z.string()).max(7).optional(),
              prompt: z
                .string()
                .describe(
                  "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                )
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(3)
          .optional(),
        addOnDetails: z
          .array(
            z.object({
              addOnId: z
                .string()
                .describe('ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                )
                .optional()
                .nullable(),
            })
          )
          .max(50)
          .optional(),
        taxableAddress: z
          .object({
            taxableAddressType: z
              .enum(['BUSINESS', 'BILLING'])
              .describe('Taxable address type.')
              .optional(),
          })
          .describe('Taxable address used to calculate tax')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items returned in the current response page.\n\nThis count reflects the actual number of items in the current result set,\nwhich may be less than the requested limit if fewer items are available.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor token for retrieving the next page of results.\n\nUse this token in subsequent requests to continue pagination forward.\nValue is `null` when on the last page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor token for retrieving the previous page of results.\n\nUse this token to navigate backwards through result pages.\nValue is `null` when on the first page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Navigation cursors for moving between result pages.\n\nContains `next` and `prev` cursor tokens for pagination. Use the `next` cursor\nto retrieve subsequent pages and `prev` cursor to go back to previous pages.\nLearn more about cursor paging in the [API Query Language guide](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates whether additional results are available beyond the current page.\n\n- `true`: More results exist and can be retrieved using the `next` cursor.\n- `false`: This is the final page of results.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Cursor-based paging metadata for navigating search results.\n\nContains the current page's cursor information, whether there are more results available,\nand count details. Use the `next` cursor to retrieve subsequent pages of results."
    )
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'User-defined name of aggregation as derived from search request.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Type of aggregation that must match specified kind as derived from search request.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'User-defined name of aggregation, matches the one specified in request.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe(
                                    'Type of aggregation that matches result.'
                                  )
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe(
                              'Date in [ISO 8601 format](https://en.wikipedia.org/wiki/ISO_8601).'
                            )
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of documents in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                scalarDate: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                                          )
                                          .max(100)
                                          .optional(),
                                      })
                                      .describe(
                                        'Scalar date aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe(
                              'Map of nested aggregation results, keyed by aggregation name.'
                            )
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe(
                        'Type of scalar date aggregation. Possible values: `MIN`, `MAX`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Date aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe(
      'Aggregation results based on the aggregations specified in the search request.\n\nProvides categorized data summaries such as service counts by type, location distribution,\npayment method statistics, or custom aggregations. Available only when aggregations\nare requested in the search criteria.'
    )
    .optional(),
});
export const QueryPoliciesRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(50)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Information about filters, paging, and sorting.'),
});
export const QueryPoliciesResponse = z.object({
  bookingPolicies: z
    .array(
      z.object({
        bookingPolicy: z
          .object({
            _id: z
              .string()
              .describe('The ID to the policy for the booking.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the policy was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the policy was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the policy.')
              .max(400)
              .optional()
              .nullable(),
            customPolicyDescription: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                  )
                  .optional(),
                description: z
                  .string()
                  .describe(
                    'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                  )
                  .max(2500)
                  .optional(),
              })
              .describe(
                'Custom description for the policy. This policy is displayed to the participant.'
              )
              .optional(),
            default: z
              .boolean()
              .describe('Whether the policy is the default for the meta site.')
              .optional()
              .nullable(),
            limitEarlyBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                  )
                  .optional(),
                earliestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting early bookings.')
              .optional(),
            limitLateBookingPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                  )
                  .optional(),
                latestBookingInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy for limiting late bookings.')
              .optional(),
            bookAfterStartPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                'Policy on booking an entity after the start of the schedule.'
              )
              .optional(),
            cancellationPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestCancellation: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestCancellationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for canceling a booked entity.')
              .optional(),
            reschedulePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestReschedule: z
                  .boolean()
                  .describe(
                    'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                  )
                  .optional(),
                latestRescheduleInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                  )
                  .min(1)
                  .optional(),
                allowAnonymous: z
                  .boolean()
                  .describe(
                    "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Policy for rescheduling a booked entity.')
              .optional(),
            waitlistPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                  )
                  .optional(),
                capacity: z
                  .number()
                  .int()
                  .describe(
                    'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
                reservationTimeInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Waitlist policy for the service.')
              .optional(),
            participantsPolicy: z
              .object({
                maxParticipantsPerBooking: z
                  .number()
                  .int()
                  .describe(
                    'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Policy regarding the participants per booking.')
              .optional(),
            resourcesPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                  )
                  .optional(),
                autoAssignAllowed: z
                  .boolean()
                  .describe(
                    '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe('Policy for allocating resources.')
              .optional(),
            cancellationFeePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                  )
                  .optional(),
                cancellationWindows: z
                  .array(
                    z.intersection(
                      z.object({
                        startInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'The fee will be applied if the booked session starts within this start time in minutes.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Amount to be charged as a cancellation fee.'
                            ),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              'Percentage of the original price to be charged as a cancellation fee.'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(2)
                  .optional(),
                autoCollectFeeEnabled: z
                  .boolean()
                  .describe(
                    'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Rules for cancellation fees.')
              .optional(),
            saveCreditCardPolicy: z
              .object({
                enabled: z.boolean().describe('Default: `false`').optional(),
              })
              .describe('Rule for saving credit card.')
              .optional(),
          })
          .describe('Retrieved booking policy.')
          .optional(),
        connectedServices: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Service ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              appId: z
                .string()
                .describe(
                  'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              createdByAppId: z
                .string()
                .describe(
                  "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              type: z
                .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
                .describe(
                  'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
                )
                .optional(),
              sortOrder: z
                .number()
                .int()
                .describe(
                  'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Service name.')
                .max(400)
                .min(1)
                .optional()
                .nullable(),
              description: z
                .string()
                .describe(
                  'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
                )
                .max(7000)
                .optional()
                .nullable(),
              tagLine: z
                .string()
                .describe('Short service description, such as `Hair styling`.')
                .max(6000)
                .optional()
                .nullable(),
              defaultCapacity: z
                .number()
                .int()
                .describe(
                  'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              media: z
                .object({
                  items: z
                    .array(
                      z.intersection(
                        z.object({}),
                        z.xor([
                          z.object({ image: z.never().optional() }),
                          z.object({
                            image: z
                              .string()
                              .describe(
                                'Details of the image associated with the service, such as URL and size.'
                              ),
                          }),
                        ])
                      )
                    )
                    .max(100)
                    .optional(),
                  mainMedia: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ image: z.never().optional() }),
                        z.object({
                          image: z
                            .string()
                            .describe(
                              'Details of the image associated with the service, such as URL and size.'
                            ),
                        }),
                      ])
                    )
                    .describe('Primary media associated with the service.')
                    .optional(),
                  coverMedia: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ image: z.never().optional() }),
                        z.object({
                          image: z
                            .string()
                            .describe(
                              'Details of the image associated with the service, such as URL and size.'
                            ),
                        }),
                      ])
                    )
                    .describe('Cover media associated with the service.')
                    .optional(),
                })
                .describe('Media associated with the service.')
                .optional(),
              hidden: z
                .boolean()
                .describe(
                  'Whether the service is hidden from Wix Bookings pages and widgets.'
                )
                .optional()
                .nullable(),
              category: z
                .object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  name: z
                    .string()
                    .describe('Category name.')
                    .max(500)
                    .optional()
                    .nullable(),
                  sortOrder: z
                    .number()
                    .int()
                    .describe('Order of a category within a category list.')
                    .optional()
                    .nullable(),
                })
                .describe(
                  "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
                )
                .optional(),
              form: z
                .object({
                  _id: z
                    .string()
                    .describe(
                      'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                })
                .describe(
                  'Form the customer filled out when booking the service.'
                )
                .optional(),
              payment: z
                .intersection(
                  z.object({
                    rateType: z
                      .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
                      .describe(
                        'The rate the customer is expected to pay for the service.'
                      )
                      .optional(),
                    options: z
                      .object({
                        online: z
                          .boolean()
                          .describe(
                            'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                          )
                          .optional()
                          .nullable(),
                        inPerson: z
                          .boolean()
                          .describe(
                            'Customers can pay for the service in person.'
                          )
                          .optional()
                          .nullable(),
                        deposit: z
                          .boolean()
                          .describe(
                            'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                          )
                          .optional()
                          .nullable(),
                        pricingPlan: z
                          .boolean()
                          .describe(
                            'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'The payment options a customer can use to pay for the service.'
                      )
                      .optional(),
                    pricingPlanIds: z.array(z.string()).max(75).optional(),
                    addOnOption: z
                      .enum(['ONLINE', 'IN_PERSON'])
                      .describe(
                        'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      fixed: z.never().optional(),
                      custom: z.never().optional(),
                      varied: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      varied: z.never().optional(),
                      fixed: z
                        .object({
                          price: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                            )
                            .optional(),
                          deposit: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                            )
                            .optional(),
                          fullUpfrontPaymentAllowed: z
                            .boolean()
                            .describe(
                              'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                        ),
                    }),
                    z.object({
                      fixed: z.never().optional(),
                      varied: z.never().optional(),
                      custom: z
                        .object({
                          description: z
                            .string()
                            .describe(
                              'A custom description explaining to the customer how to pay for the service.'
                            )
                            .max(50)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                        ),
                    }),
                    z.object({
                      fixed: z.never().optional(),
                      custom: z.never().optional(),
                      varied: z
                        .object({
                          defaultPrice: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                            )
                            .optional(),
                          deposit: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                            )
                            .optional(),
                          minPrice: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'The minimal price a customer may pay for this service, based on its variants.'
                            )
                            .optional(),
                          maxPrice: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'The maximum price a customer may pay for this service, based on its variants.'
                            )
                            .optional(),
                          fullUpfrontPaymentAllowed: z
                            .boolean()
                            .describe(
                              'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                        ),
                    }),
                  ])
                )
                .describe(
                  'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
                )
                .optional(),
              onlineBooking: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
                    )
                    .optional()
                    .nullable(),
                  requireManualApproval: z
                    .boolean()
                    .describe(
                      'Booking the service requires approval by the Wix user.'
                    )
                    .optional()
                    .nullable(),
                  allowMultipleRequests: z
                    .boolean()
                    .describe(
                      'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Online booking settings.')
                .optional(),
              conferencing: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      "Whether a conference link is generated for the service's sessions."
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Conferencing options for the service.')
                .optional(),
              locations: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Location ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      type: z
                        .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                        .describe('Location type.\n\nDefault: `CUSTOM`')
                        .optional(),
                      calculatedAddress: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                        })
                        .describe(
                          'Location address. Empty for `{"type": "CUSTOMER"}`.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        business: z.never().optional(),
                        custom: z.never().optional(),
                      }),
                      z.object({
                        custom: z.never().optional(),
                        business: z
                          .object({
                            _id: z
                              .string()
                              .describe(
                                'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .string()
                              .describe('Business location name.')
                              .max(150)
                              .optional(),
                            default: z
                              .boolean()
                              .describe(
                                'Whether this is the default location. There can only be a single default location per site.'
                              )
                              .optional()
                              .nullable(),
                            address: z
                              .object({
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine1: z.string().optional().nullable(),
                              })
                              .describe('Business location address.')
                              .optional(),
                            email: z
                              .string()
                              .describe('Business location email.')
                              .email()
                              .optional()
                              .nullable(),
                            phone: z
                              .string()
                              .describe('Business location phone.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Information about business locations.'),
                      }),
                      z.object({
                        business: z.never().optional(),
                        custom: z
                          .object({
                            _id: z
                              .string()
                              .describe('ID of the custom location.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            address: z
                              .object({
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine1: z.string().optional().nullable(),
                              })
                              .describe('Address of the custom location.')
                              .optional(),
                          })
                          .describe('Information about custom locations.'),
                      }),
                    ])
                  )
                )
                .max(500)
                .optional(),
              bookingPolicy: z
                .object({
                  _id: z
                    .string()
                    .describe('The ID to the policy for the booking.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the policy was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe('Date and time the policy was updated.')
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Name of the policy.')
                    .max(400)
                    .optional()
                    .nullable(),
                  customPolicyDescription: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                        )
                        .max(2500)
                        .optional(),
                    })
                    .describe(
                      'Custom description for the policy. This policy is displayed to the participant.'
                    )
                    .optional(),
                  default: z
                    .boolean()
                    .describe(
                      'Whether the policy is the default for the meta site.'
                    )
                    .optional()
                    .nullable(),
                  limitEarlyBookingPolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                        )
                        .optional(),
                      earliestBookingInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                        )
                        .min(1)
                        .optional(),
                    })
                    .describe('Policy for limiting early bookings.')
                    .optional(),
                  limitLateBookingPolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                        )
                        .optional(),
                      latestBookingInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                        )
                        .min(1)
                        .optional(),
                    })
                    .describe('Policy for limiting late bookings.')
                    .optional(),
                  bookAfterStartPolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      'Policy on booking an entity after the start of the schedule.'
                    )
                    .optional(),
                  cancellationPolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                        )
                        .optional(),
                      limitLatestCancellation: z
                        .boolean()
                        .describe(
                          'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                        )
                        .optional(),
                      latestCancellationInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                        )
                        .min(1)
                        .optional(),
                      allowAnonymous: z
                        .boolean()
                        .describe(
                          "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Policy for canceling a booked entity.')
                    .optional(),
                  reschedulePolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                        )
                        .optional(),
                      limitLatestReschedule: z
                        .boolean()
                        .describe(
                          'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                        )
                        .optional(),
                      latestRescheduleInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                        )
                        .min(1)
                        .optional(),
                      allowAnonymous: z
                        .boolean()
                        .describe(
                          "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Policy for rescheduling a booked entity.')
                    .optional(),
                  waitlistPolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                        )
                        .optional(),
                      capacity: z
                        .number()
                        .int()
                        .describe(
                          'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                        )
                        .min(1)
                        .optional(),
                      reservationTimeInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                        )
                        .min(1)
                        .optional(),
                    })
                    .describe('Waitlist policy for the service.')
                    .optional(),
                  participantsPolicy: z
                    .object({
                      maxParticipantsPerBooking: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                        )
                        .min(1)
                        .optional(),
                    })
                    .describe('Policy regarding the participants per booking.')
                    .optional(),
                  resourcesPolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                        )
                        .optional(),
                      autoAssignAllowed: z
                        .boolean()
                        .describe(
                          '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe('Policy for allocating resources.')
                    .optional(),
                  cancellationFeePolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                        )
                        .optional(),
                      cancellationWindows: z
                        .array(
                          z.intersection(
                            z.object({
                              startInMinutes: z
                                .number()
                                .int()
                                .describe(
                                  'The fee will be applied if the booked session starts within this start time in minutes.'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({
                                amount: z.never().optional(),
                                percentage: z.never().optional(),
                              }),
                              z.object({
                                percentage: z.never().optional(),
                                amount: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                                      )
                                      .optional(),
                                    currency: z
                                      .string()
                                      .describe(
                                        'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                                      )
                                      .optional(),
                                    formattedValue: z
                                      .string()
                                      .describe(
                                        'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                                      )
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Amount to be charged as a cancellation fee.'
                                  ),
                              }),
                              z.object({
                                amount: z.never().optional(),
                                percentage: z
                                  .string()
                                  .describe(
                                    'Percentage of the original price to be charged as a cancellation fee.'
                                  ),
                              }),
                            ])
                          )
                        )
                        .max(2)
                        .optional(),
                      autoCollectFeeEnabled: z
                        .boolean()
                        .describe(
                          'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Rules for cancellation fees.')
                    .optional(),
                  saveCreditCardPolicy: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe('Default: `false`')
                        .optional(),
                    })
                    .describe('Rule for saving credit card.')
                    .optional(),
                })
                .describe(
                  '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
                )
                .optional(),
              schedule: z
                .object({
                  _id: z
                    .string()
                    .describe(
                      "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  firstSessionStart: z
                    .date()
                    .describe(
                      'Start time of the first session in the schedule. For courses only.'
                    )
                    .optional()
                    .nullable(),
                  lastSessionEnd: z
                    .date()
                    .describe(
                      'End time of the last session in the schedule. For courses only.'
                    )
                    .optional()
                    .nullable(),
                  availabilityConstraints: z
                    .object({
                      durations: z
                        .array(
                          z.object({
                            minutes: z
                              .number()
                              .int()
                              .describe(
                                'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                              )
                              .min(1)
                              .max(44639)
                              .optional(),
                          })
                        )
                        .max(50)
                        .optional(),
                      sessionDurations: z
                        .array(z.number().int())
                        .max(50)
                        .optional(),
                      timeBetweenSessions: z
                        .number()
                        .int()
                        .describe(
                          'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                        )
                        .min(0)
                        .max(720)
                        .optional(),
                    })
                    .describe('Limitations affecting the service availability.')
                    .optional(),
                })
                .describe(
                  "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
                )
                .optional(),
              staffMemberIds: z.array(z.string()).max(220).optional(),
              serviceResources: z
                .array(
                  z.object({
                    resourceType: z
                      .object({
                        _id: z
                          .string()
                          .describe('The type of the resource.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('The name of the resource type.')
                          .max(40)
                          .min(1)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                      )
                      .optional(),
                  })
                )
                .max(3)
                .optional(),
              supportedSlugs: z
                .array(
                  z.object({
                    name: z
                      .string()
                      .describe(
                        "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                      )
                      .max(500)
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                      )
                      .optional()
                      .nullable(),
                    _createdDate: z
                      .date()
                      .describe(
                        'Date and time the slug was created. This is a system field.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(100)
                .optional(),
              mainSlug: z
                .object({
                  name: z
                    .string()
                    .describe(
                      "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
                    )
                    .max(500)
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe(
                      'Date and time the slug was created. This is a system field.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
                )
                .optional(),
              urls: z
                .object({
                  servicePage: z
                    .string()
                    .describe('The URL for the service page.')
                    .optional(),
                  bookingPage: z
                    .string()
                    .describe(
                      'The URL for the booking entry point. It can be either to the calendar or to the service page.'
                    )
                    .optional(),
                  calendarPage: z
                    .string()
                    .describe(
                      'The URL for the calendar. Can be empty if no calendar exists.'
                    )
                    .optional(),
                })
                .describe(
                  'URLs to various service-related pages, such as the calendar page and the booking page.'
                )
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Extensions enabling users to save custom data related to the service.'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Custom SEO data for the service.')
                .optional(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
                )
                .optional()
                .nullable(),
              addOnGroups: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe('Name of the add-on group.')
                      .max(100)
                      .optional()
                      .nullable(),
                    maxNumberOfAddOns: z
                      .number()
                      .int()
                      .describe(
                        "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
                      )
                      .optional()
                      .nullable(),
                    addOnIds: z.array(z.string()).max(7).optional(),
                    prompt: z
                      .string()
                      .describe(
                        "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(3)
                .optional(),
              addOnDetails: z
                .array(
                  z.object({
                    addOnId: z
                      .string()
                      .describe('ID of the add-on.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe(
                        'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
              taxableAddress: z
                .object({
                  taxableAddressType: z
                    .enum(['BUSINESS', 'BILLING'])
                    .describe('Taxable address type.')
                    .optional(),
                })
                .describe('Taxable address used to calculate tax')
                .optional(),
            })
          )
          .max(5)
          .optional(),
        totalServiceCount: z
          .number()
          .int()
          .describe('Total number of services connected to the booking policy.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items returned in the current response page.\n\nThis count reflects the actual number of items in the current result set,\nwhich may be less than the requested limit if fewer items are available.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor token for retrieving the next page of results.\n\nUse this token in subsequent requests to continue pagination forward.\nValue is `null` when on the last page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor token for retrieving the previous page of results.\n\nUse this token to navigate backwards through result pages.\nValue is `null` when on the first page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Navigation cursors for moving between result pages.\n\nContains `next` and `prev` cursor tokens for pagination. Use the `next` cursor\nto retrieve subsequent pages and `prev` cursor to go back to previous pages.\nLearn more about cursor paging in the [API Query Language guide](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates whether additional results are available beyond the current page.\n\n- `true`: More results exist and can be retrieved using the `next` cursor.\n- `false`: This is the final page of results.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const QueryBookingFormsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(50)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe(
      'Information about filters, paging, and sorting. See the article about\nform filters ([REST](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object))\nfor all supported filters and sorting options.'
    ),
  options: z
    .object({
      conditionalFields: z
        .array(z.enum(['DEFAULT_BOOKING_FORM']))
        .max(5)
        .optional(),
    })
    .optional(),
});
export const QueryBookingFormsResponse = z.object({
  bookingForms: z
    .array(
      z.object({
        formDetails: z
          .object({
            formId: z
              .string()
              .describe('Form ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            name: z
              .string()
              .describe('Form name.')
              .max(200)
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the form is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the form.'
              )
              .optional()
              .nullable(),
          })
          .describe('Information about the retrieved booking form.')
          .optional(),
        connectedServices: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the service connected to the booking form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Name of the service connected to the booking form.')
                .max(400)
                .min(1)
                .optional()
                .nullable(),
            })
          )
          .max(5)
          .optional(),
        totalServiceCount: z
          .number()
          .int()
          .describe('Total number of services connected to the booking form.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items returned in the current response page.\n\nThis count reflects the actual number of items in the current result set,\nwhich may be less than the requested limit if fewer items are available.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor token for retrieving the next page of results.\n\nUse this token in subsequent requests to continue pagination forward.\nValue is `null` when on the last page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor token for retrieving the previous page of results.\n\nUse this token to navigate backwards through result pages.\nValue is `null` when on the first page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Navigation cursors for moving between result pages.\n\nContains `next` and `prev` cursor tokens for pagination. Use the `next` cursor\nto retrieve subsequent pages and `prev` cursor to go back to previous pages.\nLearn more about cursor paging in the [API Query Language guide](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates whether additional results are available beyond the current page.\n\n- `true`: More results exist and can be retrieved using the `next` cursor.\n- `false`: This is the final page of results.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
  defaultBookingForm: z
    .object({
      formDetails: z
        .object({
          formId: z
            .string()
            .describe('Form ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Form name.')
            .max(200)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the form is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the form.'
            )
            .optional()
            .nullable(),
        })
        .describe('Information about the retrieved booking form.')
        .optional(),
      connectedServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('ID of the service connected to the booking form.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the service connected to the booking form.')
              .max(400)
              .min(1)
              .optional()
              .nullable(),
          })
        )
        .max(5)
        .optional(),
      totalServiceCount: z
        .number()
        .int()
        .describe('Total number of services connected to the booking form.')
        .optional(),
    })
    .describe(
      "The site's default booking form and information about connected services."
    )
    .optional(),
});
export const CountServicesRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Query filter to base the count on. See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CountServicesResponse = z.object({
  count: z
    .number()
    .int()
    .describe('Number of services matching the specified filter.')
    .optional(),
});
export const QueryLocationsRequest = z.object({
  options: z
    .object({
      filter: z
        .object({
          services: z
            .record(z.string(), z.any())
            .describe(
              'Service filter. See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters.'
            )
            .optional()
            .nullable(),
          businessLocationIds: z.array(z.string()).max(100).optional(),
        })
        .describe('Filter.')
        .optional(),
    })
    .optional(),
});
export const QueryLocationsResponse = z.object({
  businessLocations: z
    .object({
      exists: z
        .boolean()
        .describe(
          'Whether at least one service matching the filter is connected to any of the\nretrieved business locations.'
        )
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z
                .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                .describe('Location type.\n\nDefault: `CUSTOM`')
                .optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
    })
    .describe(
      'Retrieved business locations and whether each location is connected to at\nleast one service.'
    )
    .optional(),
  customLocations: z
    .object({
      exists: z
        .boolean()
        .describe(
          'Whether at least one service matching the filter is connected to any of the\nretrieved custom locations.'
        )
        .optional(),
    })
    .describe(
      'Retrieved custom locations and whether each location is connected to at\nleast one service.'
    )
    .optional(),
  customerLocations: z
    .object({
      exists: z
        .boolean()
        .describe(
          'Whether at least one service matching the filter is connected to any of the\nretrieved customer locations.'
        )
        .optional(),
    })
    .describe(
      'Retrieved customer locations and whether each location is connected to at\nleast one service.'
    )
    .optional(),
});
export const QueryCategoriesRequest = z.object({
  options: z
    .object({
      filter: z
        .object({
          services: z
            .record(z.string(), z.any())
            .describe(
              'Service filter. See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters.'
            )
            .optional()
            .nullable(),
          categoryIds: z.array(z.string()).max(100).optional(),
        })
        .describe('Filter.')
        .optional(),
    })
    .optional(),
});
export const QueryCategoriesResponse = z.object({
  categories: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Category ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z
          .string()
          .describe('Category name.')
          .max(500)
          .optional()
          .nullable(),
        sortOrder: z
          .number()
          .int()
          .describe('Order of a category within a category list.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const SetServiceLocationsRequest = z.object({
  serviceId: z
    .string()
    .describe('ID of the service.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z.enum(['CUSTOM', 'BUSINESS', 'CUSTOMER']).optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
      removedLocationSessionsAction: z
        .intersection(
          z.object({
            action: z
              .enum(['KEEP_AT_CURRENT_LOCATION', 'MOVE_TO_LOCATION'])
              .optional(),
          }),
          z.xor([
            z.object({ moveToLocationOptions: z.never().optional() }),
            z.object({
              moveToLocationOptions: z
                .object({
                  newLocation: z
                    .intersection(
                      z.object({
                        _id: z
                          .string()
                          .describe('Location ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        type: z
                          .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                          .optional(),
                        calculatedAddress: z
                          .object({
                            city: z.string().optional().nullable(),
                            subdivision: z.string().optional().nullable(),
                            country: z.string().optional().nullable(),
                            postalCode: z.string().optional().nullable(),
                            addressLine1: z.string().optional().nullable(),
                          })
                          .describe(
                            'Location address. Empty for `{"type": "CUSTOMER"}`.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          business: z.never().optional(),
                          custom: z.never().optional(),
                        }),
                        z.object({
                          custom: z.never().optional(),
                          business: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe('Business location name.')
                                .max(150)
                                .optional(),
                              default: z
                                .boolean()
                                .describe(
                                  'Whether this is the default location. There can only be a single default location per site.'
                                )
                                .optional()
                                .nullable(),
                              address: z
                                .object({
                                  city: z.string().optional().nullable(),
                                  subdivision: z.string().optional().nullable(),
                                  country: z.string().optional().nullable(),
                                  postalCode: z.string().optional().nullable(),
                                  addressLine1: z
                                    .string()
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Business location address.')
                                .optional(),
                              email: z
                                .string()
                                .describe('Business location email.')
                                .email()
                                .optional()
                                .nullable(),
                              phone: z
                                .string()
                                .describe('Business location phone.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Information about business locations.'),
                        }),
                        z.object({
                          business: z.never().optional(),
                          custom: z
                            .object({
                              _id: z
                                .string()
                                .describe('ID of the custom location.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              address: z
                                .object({
                                  city: z.string().optional().nullable(),
                                  subdivision: z.string().optional().nullable(),
                                  country: z.string().optional().nullable(),
                                  postalCode: z.string().optional().nullable(),
                                  addressLine1: z
                                    .string()
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Address of the custom location.')
                                .optional(),
                            })
                            .describe('Information about custom locations.'),
                        }),
                      ])
                    )
                    .describe(
                      'The new location to move existing sessions currently set to a removed location, used when `action` is `MOVE_TO_LOCATION`.'
                    )
                    .optional(),
                })
                .describe(
                  'Details about the new location of future events that were scheduled to take\nplace at a removed location.'
                ),
            }),
          ])
        )
        .describe(
          'The action to perform on sessions currently set to a removed location. For\nexample, move existing sessions to a new specified location.\nIf not specified, sessions will not be moved to a new location.'
        )
        .optional(),
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Custom message to send to the participants about the changes to the booking.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Whether to notify participants about the change of location, and an\nOptional custom message. The notification is sent only to participants of sessions that are affected by the change.'
        )
        .optional(),
    })
    .optional(),
});
export const SetServiceLocationsResponse = z.object({
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
        .describe(
          'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
        )
        .optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .describe(
                'The rate the customer is expected to pay for the service.'
              )
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z
              .enum(['ONLINE', 'IN_PERSON'])
              .describe(
                'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z
                .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                .describe('Location type.\n\nDefault: `CUSTOM`')
                .optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        )
        .optional()
        .nullable(),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z
            .enum(['BUSINESS', 'BILLING'])
            .describe('Taxable address type.')
            .optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('The updated service with the newly set locations.')
    .optional(),
});
export const EnablePricingPlansForServiceRequest = z.object({
  serviceId: z
    .string()
    .describe('ID of the service to update.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  pricingPlanIds: z.array(z.string()).max(100),
});
export const EnablePricingPlansForServiceResponse = z.object({
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
        .describe(
          'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
        )
        .optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .describe(
                'The rate the customer is expected to pay for the service.'
              )
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z
              .enum(['ONLINE', 'IN_PERSON'])
              .describe(
                'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z
                .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                .describe('Location type.\n\nDefault: `CUSTOM`')
                .optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        )
        .optional()
        .nullable(),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z
            .enum(['BUSINESS', 'BILLING'])
            .describe('Taxable address type.')
            .optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('Updated service.')
    .optional(),
});
export const DisablePricingPlansForServiceRequest = z.object({
  serviceId: z
    .string()
    .describe('ID of the service to update.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ pricingPlanIds: z.array(z.string()).max(75).optional() })
    .optional(),
});
export const DisablePricingPlansForServiceResponse = z.object({
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
        .describe(
          'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
        )
        .optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .describe(
                'The rate the customer is expected to pay for the service.'
              )
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z
              .enum(['ONLINE', 'IN_PERSON'])
              .describe(
                'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z
                .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                .describe('Location type.\n\nDefault: `CUSTOM`')
                .optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        )
        .optional()
        .nullable(),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z
            .enum(['BUSINESS', 'BILLING'])
            .describe('Taxable address type.')
            .optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('Updated service.')
    .optional(),
});
export const SetCustomSlugRequest = z.object({
  serviceId: z
    .string()
    .describe('ID of the service for which to update the active slug.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      slug: z
        .string()
        .describe('Slug to set as the active service slug.')
        .max(500)
        .optional(),
    })
    .optional(),
});
export const SetCustomSlugResponse = z.object({
  slug: z
    .object({
      name: z
        .string()
        .describe(
          "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
        )
        .max(500)
        .optional(),
      custom: z
        .boolean()
        .describe(
          'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the slug was created. This is a system field.')
        .optional()
        .nullable(),
    })
    .describe('Updated active service slug.')
    .optional(),
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
        .describe(
          'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
        )
        .optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .describe(
                'The rate the customer is expected to pay for the service.'
              )
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z
              .enum(['ONLINE', 'IN_PERSON'])
              .describe(
                'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z
                .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                .describe('Location type.\n\nDefault: `CUSTOM`')
                .optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        )
        .optional()
        .nullable(),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z
            .enum(['BUSINESS', 'BILLING'])
            .describe('Taxable address type.')
            .optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('Updated service.')
    .optional(),
});
export const ValidateSlugRequest = z.object({
  serviceId: z
    .string()
    .describe('IO of the service to check custom slug validity for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      slug: z.string().describe('Custom slug to validate.').max(500).optional(),
    })
    .optional(),
});
export const ValidateSlugResponse = z.object({
  valid: z.boolean().describe('Whether the slug is valid.').optional(),
  slug: z
    .string()
    .describe('Valid slug. Available only if `{"valid": true}`.')
    .max(500)
    .optional()
    .nullable(),
  errors: z
    .array(z.enum(['SLUG_CONTAINS_ILLEGAL_CHARACTERS', 'SLUG_ALREADY_EXISTS']))
    .max(3)
    .optional(),
});
export const CloneServiceRequest = z.object({
  sourceServiceId: z
    .string()
    .describe('ID of the service to clone.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CloneServiceResponse = z.object({
  service: z
    .object({
      _id: z
        .string()
        .describe('Service ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      appId: z
        .string()
        .describe(
          'ID of the app associated with the service. You can\'t update `appId`.\nServices are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.\nDefault: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)\nFor services from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      createdByAppId: z
        .string()
        .describe(
          "ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during service creation by checking these sources in order:\n1. The caller's App ID from the request identity context.\n2. The service's `appId` field.\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['APPOINTMENT', 'CLASS', 'COURSE'])
        .describe(
          'Service type.\nLearn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).'
        )
        .optional(),
      sortOrder: z
        .number()
        .int()
        .describe(
          'Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object).'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Service name.')
        .max(400)
        .min(1)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Service description. For example, `High-class hair styling, cuts, straightening and color`.'
        )
        .max(7000)
        .optional()
        .nullable(),
      tagLine: z
        .string()
        .describe('Short service description, such as `Hair styling`.')
        .max(6000)
        .optional()
        .nullable(),
      defaultCapacity: z
        .number()
        .int()
        .describe(
          'Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      media: z
        .object({
          items: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({ image: z.never().optional() }),
                  z.object({
                    image: z
                      .string()
                      .describe(
                        'Details of the image associated with the service, such as URL and size.'
                      ),
                  }),
                ])
              )
            )
            .max(100)
            .optional(),
          mainMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Primary media associated with the service.')
            .optional(),
          coverMedia: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({ image: z.never().optional() }),
                z.object({
                  image: z
                    .string()
                    .describe(
                      'Details of the image associated with the service, such as URL and size.'
                    ),
                }),
              ])
            )
            .describe('Cover media associated with the service.')
            .optional(),
        })
        .describe('Media associated with the service.')
        .optional(),
      hidden: z
        .boolean()
        .describe(
          'Whether the service is hidden from Wix Bookings pages and widgets.'
        )
        .optional()
        .nullable(),
      category: z
        .object({
          _id: z
            .string()
            .describe('Category ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Category name.')
            .max(500)
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .int()
            .describe('Order of a category within a category list.')
            .optional()
            .nullable(),
        })
        .describe(
          "[Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)\nthe service is associated with. Services aren't automatically assigned to a category.\nWithout an associated category, the service isn't visible on the live site."
        )
        .optional(),
      form: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the form associated with the service.\nThe form information that you submit when booking includes contact details, participants, and other form fields set up for the service.\nYou can manage the service booking form fields using the Bookings Forms API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Form the customer filled out when booking the service.')
        .optional(),
      payment: z
        .intersection(
          z.object({
            rateType: z
              .enum(['FIXED', 'CUSTOM', 'VARIED', 'NO_FEE'])
              .describe(
                'The rate the customer is expected to pay for the service.'
              )
              .optional(),
            options: z
              .object({
                online: z
                  .boolean()
                  .describe(
                    'Customers can pay for the service online.\nWhen `true`:\n+ `rateType` must be `FIXED` or `VARIED`.\n+ `fixed.price` or `varied.defaultPrice` must be specified respectively.\nRead more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).'
                  )
                  .optional()
                  .nullable(),
                inPerson: z
                  .boolean()
                  .describe('Customers can pay for the service in person.')
                  .optional()
                  .nullable(),
                deposit: z
                  .boolean()
                  .describe(
                    'This service requires a deposit to be made online in order to book it.\nWhen `true`:\n+ `rateType` must be `VARIED` or `FIXED`.\n+ A `deposit` must be specified.'
                  )
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .boolean()
                  .describe(
                    'Whether customers can pay for the service using a pricing plan.\nRead more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'The payment options a customer can use to pay for the service.'
              )
              .optional(),
            pricingPlanIds: z.array(z.string()).max(75).optional(),
            addOnOption: z
              .enum(['ONLINE', 'IN_PERSON'])
              .describe(
                'How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).\nIf customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z.never().optional(),
            }),
            z.object({
              custom: z.never().optional(),
              varied: z.never().optional(),
              fixed: z
                .object({
                  price: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The fixed price required to book the service.\n\nRequired when: `rateType` is `FIXED`'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the fixed price of the service.\n\nRequired when: `rateType` is `FIXED`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              varied: z.never().optional(),
              custom: z
                .object({
                  description: z
                    .string()
                    .describe(
                      'A custom description explaining to the customer how to pay for the service.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the custom price of the service.\n\nRequired when: `rateType` is `CUSTOM`'
                ),
            }),
            z.object({
              fixed: z.never().optional(),
              custom: z.never().optional(),
              varied: z
                .object({
                  defaultPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The default price for the service without any variants. It will also be used as the default price for any new variant.'
                    )
                    .optional(),
                  deposit: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The deposit price required to book the service.\n\nRequired when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`'
                    )
                    .optional(),
                  minPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The minimal price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  maxPrice: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                        )
                        .optional(),
                      formattedValue: z
                        .string()
                        .describe(
                          'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'The maximum price a customer may pay for this service, based on its variants.'
                    )
                    .optional(),
                  fullUpfrontPaymentAllowed: z
                    .boolean()
                    .describe(
                      'Whether customers can choose to pay the full service price upfront instead of only the deposit.\n\nUsed only when a `deposit` amount is set.\n\nDefault: `false`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'The details for the varied pricing of the service.\nRead more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).\n\nRequired when: `rateType` is `VARIED`'
                ),
            }),
          ])
        )
        .describe(
          'Payment options for booking the service.\nLearn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).'
        )
        .optional(),
      onlineBooking: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether the service can be booked online.\nWhen set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.\nWhen set to `false`, customers cannot book the service online, and the service can only be paid for in person.'
            )
            .optional()
            .nullable(),
          requireManualApproval: z
            .boolean()
            .describe('Booking the service requires approval by the Wix user.')
            .optional()
            .nullable(),
          allowMultipleRequests: z
            .boolean()
            .describe(
              'Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Online booking settings.')
        .optional(),
      conferencing: z
        .object({
          enabled: z
            .boolean()
            .describe(
              "Whether a conference link is generated for the service's sessions."
            )
            .optional()
            .nullable(),
        })
        .describe('Conferencing options for the service.')
        .optional(),
      locations: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Location ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              type: z
                .enum(['CUSTOM', 'BUSINESS', 'CUSTOMER'])
                .describe('Location type.\n\nDefault: `CUSTOM`')
                .optional(),
              calculatedAddress: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                })
                .describe('Location address. Empty for `{"type": "CUSTOMER"}`.')
                .optional(),
            }),
            z.xor([
              z.object({
                business: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                business: z
                  .object({
                    _id: z
                      .string()
                      .describe(
                        'ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).\nWhen setting a business location, specify only the location ID. Other location details are overwritten.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .string()
                      .describe('Business location name.')
                      .max(150)
                      .optional(),
                    default: z
                      .boolean()
                      .describe(
                        'Whether this is the default location. There can only be a single default location per site.'
                      )
                      .optional()
                      .nullable(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Business location address.')
                      .optional(),
                    email: z
                      .string()
                      .describe('Business location email.')
                      .email()
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Business location phone.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about business locations.'),
              }),
              z.object({
                business: z.never().optional(),
                custom: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the custom location.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                      })
                      .describe('Address of the custom location.')
                      .optional(),
                  })
                  .describe('Information about custom locations.'),
              }),
            ])
          )
        )
        .max(500)
        .optional(),
      bookingPolicy: z
        .object({
          _id: z
            .string()
            .describe('The ID to the policy for the booking.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the policy was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the policy was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Name of the policy.')
            .max(400)
            .optional()
            .nullable(),
          customPolicyDescription: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the description should be displayed. If `true`, the description is displayed.\n\nDefault: `false`'
                )
                .optional(),
              description: z
                .string()
                .describe(
                  'The description to display.\n\nDefault: Empty\nMax length: 2500 characters'
                )
                .max(2500)
                .optional(),
            })
            .describe(
              'Custom description for the policy. This policy is displayed to the participant.'
            )
            .optional(),
          default: z
            .boolean()
            .describe('Whether the policy is the default for the meta site.')
            .optional()
            .nullable(),
          limitEarlyBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how early a customer\ncan book. When `false`, there is no limit on the earliest\nbooking time and customers can book in advance, as early as they like.\n\nDefault: `false`'
                )
                .optional(),
              earliestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Maximum number of minutes before the start of the session that a booking can be made. This value must be greater\nthan `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.\n\nDefault: 10080 minutes (7 days)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting early bookings.')
            .optional(),
          limitLateBookingPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether there is a limit on how late a customer\ncan book. When `false`, there is no limit on the latest\nbooking time and customers can book up to the last minute.\n\nDefault: `false`'
                )
                .optional(),
              latestBookingInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the session that a booking can be made.\nFor a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.\nThis value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy for limiting late bookings.')
            .optional(),
          bookAfterStartPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether booking is allowed after the start of the schedule. When `true`,\ncustomers can book after the start of the schedule.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              'Policy on booking an entity after the start of the schedule.'
            )
            .optional(),
          cancellationPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking is allowed. When `true`, customers\ncan cancel the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestCancellation: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest cancellation time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestCancellationInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be canceled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this cancellation policy allows anonymous cancellations.\n\n**Important**: This flag only applies when `enabled` is `true`. If the cancellation\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot cancel\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for canceling a booked entity.')
            .optional(),
          reschedulePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether rescheduling a booking is allowed. When `true`, customers\ncan reschedule the booking.\n\nDefault: `false`'
                )
                .optional(),
              limitLatestReschedule: z
                .boolean()
                .describe(
                  'Whether there is a limit on the latest reschedule time. When `true`,\na time limit is enforced.\n\nDefault: `false`'
                )
                .optional(),
              latestRescheduleInMinutes: z
                .number()
                .int()
                .describe(
                  'Minimum number of minutes before the start of the booked session that the booking can be rescheduled.\n\nDefault: 1440 minutes (1 day)\nMin: 1 minute'
                )
                .min(1)
                .optional(),
              allowAnonymous: z
                .boolean()
                .describe(
                  "Whether this reschedule policy allows anonymous rescheduling.\n\n**Important**: This flag only applies when `enabled` is `true`. If the reschedule\npolicy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule\nregardless of this flag's value.\n\nWhen not set (null), defaults to disabled.\n\nDefault: `null` (treated as disabled)"
                )
                .optional()
                .nullable(),
            })
            .describe('Policy for rescheduling a booked entity.')
            .optional(),
          waitlistPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether the session has a waitlist. If `true`, there is a waitlist.\n\nDefault: `false`'
                )
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of spots available in the waitlist.\n\nDefault: 10 spots\nMin: 1 spot'
                )
                .min(1)
                .optional(),
              reservationTimeInMinutes: z
                .number()
                .int()
                .describe(
                  'Amount of time a participant is given to book, once notified that a spot is available.\n\nDefault: 10 minutes\nMin: 1 spot'
                )
                .min(1)
                .optional(),
            })
            .describe('Waitlist policy for the service.')
            .optional(),
          participantsPolicy: z
            .object({
              maxParticipantsPerBooking: z
                .number()
                .int()
                .describe(
                  'Maximum number of participants allowed.\n\nDefault: 1 participant\nMin: 1 participant'
                )
                .min(1)
                .optional(),
            })
            .describe('Policy regarding the participants per booking.')
            .optional(),
          resourcesPolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  '`true` if this policy is enabled, `false` otherwise.\nWhen `false` then the client must always select a resource when booking an appointment.'
                )
                .optional(),
              autoAssignAllowed: z
                .boolean()
                .describe(
                  '`true`, if it is allowed to automatically assign a resource when booking an appointment,\n`false`, if the client must always select a resource.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe('Policy for allocating resources.')
            .optional(),
          cancellationFeePolicy: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether canceling a booking will result in a cancellation fee\n\nDefault: `false`'
                )
                .optional(),
              cancellationWindows: z
                .array(
                  z.intersection(
                    z.object({
                      startInMinutes: z
                        .number()
                        .int()
                        .describe(
                          'The fee will be applied if the booked session starts within this start time in minutes.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        amount: z.never().optional(),
                        percentage: z.never().optional(),
                      }),
                      z.object({
                        percentage: z.never().optional(),
                        amount: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                              )
                              .optional(),
                            formattedValue: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                              )
                              .max(50)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Amount to be charged as a cancellation fee.'
                          ),
                      }),
                      z.object({
                        amount: z.never().optional(),
                        percentage: z
                          .string()
                          .describe(
                            'Percentage of the original price to be charged as a cancellation fee.'
                          ),
                      }),
                    ])
                  )
                )
                .max(2)
                .optional(),
              autoCollectFeeEnabled: z
                .boolean()
                .describe(
                  'Whether the cancellation fee should not be automatically collected when customer cancels the booking.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Rules for cancellation fees.')
            .optional(),
          saveCreditCardPolicy: z
            .object({
              enabled: z.boolean().describe('Default: `false`').optional(),
            })
            .describe('Rule for saving credit card.')
            .optional(),
        })
        .describe(
          '[Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)\ndetermining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.'
        )
        .optional(),
      schedule: z
        .object({
          _id: z
            .string()
            .describe(
              "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto which the service's events belong."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          firstSessionStart: z
            .date()
            .describe(
              'Start time of the first session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          lastSessionEnd: z
            .date()
            .describe(
              'End time of the last session in the schedule. For courses only.'
            )
            .optional()
            .nullable(),
          availabilityConstraints: z
            .object({
              durations: z
                .array(
                  z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'The duration of the service in minutes.\n\nMin: `1` minute\nMax: `44639` minutes (30 days, 23 hours, and 59 minutes)'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
              sessionDurations: z.array(z.number().int()).max(50).optional(),
              timeBetweenSessions: z
                .number()
                .int()
                .describe(
                  'The number of minutes between the end of a session and the start of the next.\n\n\nMin: `0` minutes\nMax: `720` minutes'
                )
                .min(0)
                .max(720)
                .optional(),
            })
            .describe('Limitations affecting the service availability.')
            .optional(),
        })
        .describe(
          "The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),\nwhich can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)."
        )
        .optional(),
      staffMemberIds: z.array(z.string()).max(220).optional(),
      serviceResources: z
        .array(
          z.object({
            resourceType: z
              .object({
                _id: z
                  .string()
                  .describe('The type of the resource.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('The name of the resource type.')
                  .max(40)
                  .min(1)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
              )
              .optional(),
          })
        )
        .max(3)
        .optional(),
      supportedSlugs: z
        .array(
          z.object({
            name: z
              .string()
              .describe(
                "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
              )
              .max(500)
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the slug was created. This is a system field.'
              )
              .optional()
              .nullable(),
          })
        )
        .max(100)
        .optional(),
      mainSlug: z
        .object({
          name: z
            .string()
            .describe(
              "The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`."
            )
            .max(500)
            .optional(),
          custom: z
            .boolean()
            .describe(
              'Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the slug was created. This is a system field.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Active slug for the service.\nLearn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).'
        )
        .optional(),
      urls: z
        .object({
          servicePage: z
            .string()
            .describe('The URL for the service page.')
            .optional(),
          bookingPage: z
            .string()
            .describe(
              'The URL for the booking entry point. It can be either to the calendar or to the service page.'
            )
            .optional(),
          calendarPage: z
            .string()
            .describe(
              'The URL for the calendar. Can be empty if no calendar exists.'
            )
            .optional(),
        })
        .describe(
          'URLs to various service-related pages, such as the calendar page and the booking page.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the service.'
        )
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the service.')
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the service is updated. To\nprevent conflicting changes, the existing revision must be used when updating\na service.'
        )
        .optional()
        .nullable(),
      addOnGroups: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the add-on group.')
              .max(100)
              .optional()
              .nullable(),
            maxNumberOfAddOns: z
              .number()
              .int()
              .describe(
                "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
              )
              .optional()
              .nullable(),
            addOnIds: z.array(z.string()).max(7).optional(),
            prompt: z
              .string()
              .describe(
                "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
              )
              .max(200)
              .optional()
              .nullable(),
          })
        )
        .max(3)
        .optional(),
      addOnDetails: z
        .array(
          z.object({
            addOnId: z
              .string()
              .describe('ID of the add-on.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for add-ons that extend service time.\nEmpty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).'
              )
              .optional()
              .nullable(),
          })
        )
        .max(50)
        .optional(),
      taxableAddress: z
        .object({
          taxableAddressType: z
            .enum(['BUSINESS', 'BILLING'])
            .describe('Taxable address type.')
            .optional(),
        })
        .describe('Taxable address used to calculate tax')
        .optional(),
    })
    .describe('Cloned service.')
    .optional(),
  errors: z.array(z.enum(['OPTIONS_AND_VARIANTS', 'FORM'])).optional(),
});
export const CreateAddOnGroupRequest = z.object({
  addOnGroup: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Name of the add-on group.').max(100),
      maxNumberOfAddOns: z
        .number()
        .int()
        .describe(
          "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
        )
        .optional()
        .nullable(),
      addOnIds: z.array(z.string()).max(7).optional(),
      prompt: z
        .string()
        .describe(
          "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
        )
        .max(200)
        .optional()
        .nullable(),
    })
    .describe('Add-on group to create.'),
  options: z.object({
    serviceId: z
      .string()
      .describe('ID of the service to create the add-on group for.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const CreateAddOnGroupResponse = z.object({
  addOnGroup: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Name of the add-on group.')
        .max(100)
        .optional()
        .nullable(),
      maxNumberOfAddOns: z
        .number()
        .int()
        .describe(
          "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
        )
        .optional()
        .nullable(),
      addOnIds: z.array(z.string()).max(7).optional(),
      prompt: z
        .string()
        .describe(
          "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
        )
        .max(200)
        .optional()
        .nullable(),
    })
    .describe('Created add-on group.')
    .optional(),
});
export const DeleteAddOnGroupRequest = z.object({
  addOnGroupId: z
    .string()
    .describe('ID of the add-on group to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    serviceId: z
      .string()
      .describe('ID of the service from which to delete the add-on group.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const DeleteAddOnGroupResponse = z.object({});
export const UpdateAddOnGroupRequest = z.object({
  addOnGroup: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      name: z
        .string()
        .describe('Name of the add-on group.')
        .max(100)
        .optional()
        .nullable(),
      maxNumberOfAddOns: z
        .number()
        .int()
        .describe(
          "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
        )
        .optional()
        .nullable(),
      addOnIds: z.array(z.string()).max(7).optional(),
      prompt: z
        .string()
        .describe(
          "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
        )
        .max(200)
        .optional()
        .nullable(),
    })
    .describe('Add-on group to update.'),
  options: z.object({
    serviceId: z
      .string()
      .describe('ID of the service that contains the add-on group.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const UpdateAddOnGroupResponse = z.object({
  addOnGroup: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Name of the add-on group.')
        .max(100)
        .optional()
        .nullable(),
      maxNumberOfAddOns: z
        .number()
        .int()
        .describe(
          "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
        )
        .optional()
        .nullable(),
      addOnIds: z.array(z.string()).max(7).optional(),
      prompt: z
        .string()
        .describe(
          "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
        )
        .max(200)
        .optional()
        .nullable(),
    })
    .describe('Updated add-on group.')
    .optional(),
});
export const ListAddOnGroupsByServiceIdRequest = z.object({
  serviceId: z
    .string()
    .describe('ID of the service to retrieve add-on groups for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ groupIds: z.array(z.string()).max(3).optional() })
    .optional(),
});
export const ListAddOnGroupsByServiceIdResponse = z.object({
  addOnGroupsDetails: z
    .array(
      z.object({
        groupId: z
          .string()
          .describe('Group ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        maxNumberOfAddOns: z
          .number()
          .int()
          .describe(
            'Maximum number of add-ons customers can select from this group.'
          )
          .optional()
          .nullable(),
        groupName: z
          .string()
          .describe('Group name displayed to customers.')
          .max(100)
          .optional()
          .nullable(),
        addOns: z
          .array(
            z.intersection(
              z.object({
                addOnId: z
                  .string()
                  .describe('Add-on ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Add-on name displayed to customers.')
                  .max(100)
                  .optional()
                  .nullable(),
                price: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.'
                      )
                      .optional(),
                    formattedValue: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string in local format. For example, `1 000,30`.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                  })
                  .describe('Add-on price.')
                  .optional(),
                nameTranslated: z
                  .string()
                  .describe('Translated add-on name.')
                  .max(100)
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  durationInMinutes: z.never().optional(),
                  maxQuantity: z.never().optional(),
                }),
                z.object({
                  maxQuantity: z.never().optional(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'Duration in minutes for duration-based add-ons that extend booking service time.'
                    ),
                }),
                z.object({
                  durationInMinutes: z.never().optional(),
                  maxQuantity: z
                    .number()
                    .int()
                    .describe(
                      'Maximum quantity customers can purchase for quantity-based add-ons.'
                    ),
                }),
              ])
            )
          )
          .max(7)
          .optional(),
        prompt: z
          .string()
          .describe(
            'Instructional prompt displayed to customers. For example, `Choose up to 2 relaxing spa treatments to enhance your experience`. You can also use this field as a group description. For example, `Beginner-friendly rental equipment`.'
          )
          .max(200)
          .optional()
          .nullable(),
        groupNameTranslated: z
          .string()
          .describe('Translated group name.')
          .max(100)
          .optional()
          .nullable(),
        promptTranslated: z
          .string()
          .describe(
            'Instructional translated prompt displayed to customers. For example, `Choose up to 2 relaxing spa treatments to enhance your experience`. You can also use this field as a group description. For example, `Beginner-friendly rental equipment`.'
          )
          .max(200)
          .optional()
          .nullable(),
      })
    )
    .max(3)
    .optional(),
});
export const SetAddOnsForGroupRequest = z.object({
  serviceId: z
    .string()
    .describe('Service ID containing the group.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    groupId: z
      .string()
      .describe('Group ID to set add-ons for.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    addOnIds: z.array(z.string()).min(1).max(7),
  }),
});
export const SetAddOnsForGroupResponse = z.object({
  addOnGroup: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the add-on group.\nWix Bookings automatically populates this field when creating or updating an add-on group.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Name of the add-on group.')
        .max(100)
        .optional()
        .nullable(),
      maxNumberOfAddOns: z
        .number()
        .int()
        .describe(
          "Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.\nWhen empty, there's no upper limit."
        )
        .optional()
        .nullable(),
      addOnIds: z.array(z.string()).max(7).optional(),
      prompt: z
        .string()
        .describe(
          "Description or instructional prompt of the add-on group that's displayed to customers when booking the service."
        )
        .max(200)
        .optional()
        .nullable(),
    })
    .describe('Updated add-on group.')
    .optional(),
});
